// src/components/ClassSchedulerPage.jsx
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { parseTime, formatTimeFromMinutes, getWeekNumber } from '../utils/helpers.ts';
import { idStartsWith } from '../utils/typeHelpers.ts'; // Import type-safe helper
import logger from '../utils/logger';
import DayTableSimple from './DayTableSimple.tsx';
import Button from './ui/Button';
import { useScheduleDragAndDrop, KEY_SEPARATOR } from '../hooks/useScheduleDragAndDrop'; // Import the custom hook
import { useActiveDays } from '../hooks/useActiveDays';
import { useDragToCreate } from '../hooks/useDragToCreate'; // Import the new drag-to-create hook
import { exportScheduleEntries } from '../services/apiService';
import UnscheduledEntryContainer from './UnscheduledEntryContainer'; // Import the new component
import EventContainer from './EventContainer'; // Import the event container component
import SidebarDrawerToggle from './SidebarDrawerToggle'; // Import the toggle component
import ScheduleEntry from '../models/ScheduleEntry.ts'; // Add this import
import { parseWeekSeries } from '../utils/timeUtils'; // ADDED: Time utils
import { deriveScheduleTimelineContext } from '../utils/scheduleContext';
import { mergeConflictPeerEntries, decorateConflictReasonForDisplay } from '../utils/conflictPeers';
import EditScheduleItemModal from './EditScheduleItemModal'; // Import the modal component
import {
    formatDraftDisplayName,
    formatEditingDraftLabel,
    findReusableWorkingDraft,
    generateVersionName,
    getDraftTitle,
    isDraftLockError,
    isHiddenFromVersions,
    isHistoryDraft
} from '../utils/scheduleDraftKind';
import ForkLiveDraftModal from './ui/ForkLiveDraftModal';
// Room view switched to RoomsOccupancyView
import RoomsOccupancyView from './RoomsOccupancyView.jsx';
import TeacherView from './TeacherView'; // Import the new teacher view component
// Change tracking via useScheduleSaveManager
import ConflictListModal from './ConflictListModal'; // Import the conflict list modal
import { conflictManager } from '../services/ConflictManager.ts'; // Import the conflict manager
import DebugDrawer from './ClassSchedulerPage/DebugDrawer';
import ClassSchedulerHeader from './ClassSchedulerPage/ClassSchedulerHeader';
import DraftPeriodSidebar from './ClassSchedulerPage/DraftPeriodSidebar';
import DraftManagerView from './ClassSchedulerPage/DraftManagerView';
import PeriodTimelineChart from './ClassSchedulerPage/PeriodTimelineChart';
import PeriodManager from '../services/PeriodManager.ts';
import {
    TIME_FRAME_SINGLE_WEEK,
    TIME_FRAME_WHOLE_PERIOD,
    createTimelineViewRange,
    getSingleWeekFromTimelineRange
} from '../utils/timelineViewRange';
import UnsavedChangesConfirmModal from './ui/UnsavedChangesConfirmModal';
import DraftLiveConfirmModal from './ui/DraftLiveConfirmModal';
import { useClassSchedulerState } from './ClassSchedulerPage/useClassSchedulerState';
import { SharedScrollProvider } from '../contexts/SharedScrollContext';
import FloatingActionButtons from './ClassSchedulerPage/FloatingActionButtons.tsx';
import CopyDraftView from './ClassSchedulerPage/CopyDraftView.tsx';
// Icons moved to SidebarDrawerToggle component

// --- Constants for DnD ---
const ItemTypes = {
    COURSE: 'course', // Used by DraggableCourseItem
    SCHEDULED_ITEM: 'scheduled_item' // Could be used by items on DayTableSimple if needed
};

const getPeriodNumber = (period, primaryKey, fallbackKey) => {
    const raw = period?.[primaryKey] ?? (fallbackKey ? period?.[fallbackKey] : undefined);
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const getPeriodCalendarYear = (period) => {
    return getPeriodNumber(period, 'year')
        ?? getPeriodNumber(period, 'start_week_year', 'startWeekYear');
};

const getPeriodActiveDraftId = (period) => {
    const raw = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const buildDefaultDisplayNameForPeriod = (period) => {
    const rawPeriodName = period?.name || 'Schedule';
    return String(rawPeriodName)
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, '-')
        .replace(/\s+/g, ' ')
        .trim() || 'Schedule';
};

const isWeekInsidePeriod = (period, week, year) => {
    const startWeek = getPeriodNumber(period, 'start_week', 'startWeek');
    const endWeek = getPeriodNumber(period, 'end_week', 'endWeek');
    const periodYear = getPeriodCalendarYear(period);

    if (!startWeek || !endWeek || !Number.isFinite(Number(week))) {
        return false;
    }

    const weekNumber = Number(week);
    const weekYear = Number.isFinite(Number(year)) ? Number(year) : periodYear;

    if (periodYear == null) {
        return startWeek <= endWeek
            ? weekNumber >= startWeek && weekNumber <= endWeek
            : weekNumber >= startWeek || weekNumber <= endWeek;
    }

    if (startWeek <= endWeek) {
        return weekYear === periodYear && weekNumber >= startWeek && weekNumber <= endWeek;
    }

    return (weekYear === periodYear && weekNumber >= startWeek)
        || (weekYear === periodYear + 1 && weekNumber <= endWeek);
};

const isRoomHiddenFromLive = (room) => {
    const publicVisible = room?.publicVisible ?? room?.public_visible;
    return publicVisible === false;
};

const filterRoomsByVisibility = (rooms, visibilityFilter) => {
    if (visibilityFilter === 'visible') {
        return rooms.filter((room) => !isRoomHiddenFromLive(room));
    }
    if (visibilityFilter === 'hidden') {
        return rooms.filter((room) => isRoomHiddenFromLive(room));
    }
    return rooms;
};

// DebugDrawer moved to ./ClassSchedulerPage/DebugDrawer


// --- Main ClassSchedulerPage Component ---
const ClassSchedulerPage = ({ user }) => {
    // --- Hook: encapsulated state & data fetching ---
    const {
        // Services
        assetManager,
        draftManager,

        // State
        currentDraftName,
        courseDefinitions,
        isFetchingDefinitions,
        hasLoadedDefinitionsOnce,
        fetchDefinitionsError,
        teachers,
        groups,
        subjects,
        rooms,
        isLoadingAssets,
        assetsError,
        timePeriods,
        refreshTimePeriods,
        selectedPeriodId,
        setSelectedPeriodId,
        isFetchingPeriods,
        fetchPeriodsError,
        yearStructure,
        isFetchingYearStructure,
        scheduledInstances,
        setScheduledInstances: originalSetScheduledInstances,
        filteredScheduledInstances,
        setFilteredScheduledInstances,
        timelineViewRange,
        setTimelineViewRange,
        textFilterQuery,
        setTextFilterQuery,
        currentView,
        setCurrentView,
        selectedRoomForView,
        setSelectedRoomForView,
        selectedTeacherForView,
        setSelectedTeacherForView,
        selectedYearForView,
        setSelectedYearForView,
        selectedMonth,
        setSelectedMonth,
        selectedWeek,
        setSelectedWeek,
        setDraftRefreshTrigger,
        isDraftManagerInitialized,
        isBaselineSet,
        setIsBaselineSet,
        hasInitialConflictsCalculated,
        setHasInitialConflictsCalculated,
        isLoading,
        setIsLoading,
        error,
        setError,
        success,
        setSuccess,
        isImporting,
        setIsImporting,
        importError,
        setImportError,
        importSuccess,
        setImportSuccess,
        isExporting,
        setIsExporting,
        isDebugDrawerOpen,
        setIsDebugDrawerOpen,

        // Computed
        availableYears,

        // Handlers
        handleModeChange,
        handleDraftChange,
        toggleDebugDrawer
        ,
        // Save/change tracking and baseline passthrough
        addLogEntry,
        calculateNetChanges,
        handleSaveChanges,
        serverBaseline,
        saveChangesToAPI,
        handleSaveSchedule,
        getChangeIdCounts,
        hasPendingChangesByIdSets,
        refreshScheduleEntries,
        discardUnsavedChanges,
        // Undo/Redo functionality
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        getUndoRedoState,

        // Teacher management
        handleAddTeacher,

        // Subject management
        handleAddSubject,

        // Group management
        handleAddGroup
    } = useClassSchedulerState(user);

    // Wrapped setScheduledInstances with comprehensive logging
    const setScheduledInstances = useCallback((newValue) => {
        const stack = new Error().stack;
        const caller = stack?.split('\n')[2]?.trim() || 'unknown';

        console.log('*** SETSCHEDULEDINSTANCES CALLED ***', {
            timestamp: new Date().toISOString(),
            caller: caller,
            newValueType: typeof newValue,
            newValueLength: Array.isArray(newValue) ? newValue.length : 'not-array',
            currentLength: scheduledInstances.length,
            isFunction: typeof newValue === 'function',
            isArray: Array.isArray(newValue)
        });

        if (Array.isArray(newValue)) {
            console.log('*** SETSCHEDULEDINSTANCES - NEW ARRAY DETAILS ***', {
                length: newValue.length,
                sampleIds: newValue.slice(0, 5).map(item => item?.id),
                allIds: newValue.map(item => item?.id)
            });
        } else if (typeof newValue === 'function') {
            console.log('*** SETSCHEDULEDINSTANCES - FUNCTION UPDATE ***', {
                currentLength: scheduledInstances.length,
                currentSampleIds: scheduledInstances.slice(0, 5).map(item => item?.id)
            });
        }

        logger.debug('[ClassSchedulerPage] setScheduledInstances called', {
            caller: caller,
            newValueType: typeof newValue,
            newValueLength: Array.isArray(newValue) ? newValue.length : 'not-array',
            currentLength: scheduledInstances.length
        });

        return originalSetScheduledInstances(newValue);
    }, [originalSetScheduledInstances, scheduledInstances.length]);

    // State moved to useClassSchedulerState

    // --- Derived State & Memoized Values ---

    // Trace baseline and changes interaction on this page
    useEffect(() => {
        console.debug('[ClassSchedulerPage] useChangeTracking snapshot', {
            serverBaselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a'
        });
    }, [serverBaseline]);

    // Reflect hook baseline readiness into page-level flag
    useEffect(() => {
        const ready = Array.isArray(serverBaseline) && serverBaseline.length > 0;
        if (isBaselineSet !== ready) {
            setIsBaselineSet(ready);
        }
    }, [serverBaseline, isBaselineSet, setIsBaselineSet]);

    // State provided by useClassSchedulerState

    // Debug: Log when filteredScheduledInstances changes
    useEffect(() => {

        if (filteredScheduledInstances.length > 0) {
            logger.debug('[ClassSchedulerPage] Sample filtered item:', {
                id: filteredScheduledInstances[0].id,
                course: filteredScheduledInstances[0].course,
                day: filteredScheduledInstances[0].day,
                timeStart: filteredScheduledInstances[0].timeStart,
                room: filteredScheduledInstances[0].room
            });
        }
    }, [filteredScheduledInstances]);
    // State provided by useClassSchedulerState

    const [draftsForPeriodLock, setDraftsForPeriodLock] = useState([]);
    const [isForkLivePromptOpen, setIsForkLivePromptOpen] = useState(false);
    const [isForkingLiveDraft, setIsForkingLiveDraft] = useState(false);
    const [showOverlappingPeriodEntries, setShowOverlappingPeriodEntries] = useState(false);
    const [showCopyView, setShowCopyView] = useState(false);
    const [extraVisiblePeriodId, setExtraVisiblePeriodId] = useState(null);
    const [extraPeriodLiveEntries, setExtraPeriodLiveEntries] = useState([]);
    const [timeFrameMode, setTimeFrameMode] = useState(TIME_FRAME_WHOLE_PERIOD);

    const buildPeriodTimelineRange = useCallback((period) => {
        const weeks = PeriodManager.getWeeksForPeriod(period, yearStructure);
        if (!weeks.length) return null;
        const first = weeks[0];
        const last = weeks[weeks.length - 1];
        return createTimelineViewRange({
            startWeek: first.week,
            startWeekYear: first.year,
            endWeek: last.week,
            endWeekYear: last.year
        });
    }, [yearStructure]);

    const timeFrameSelection = useMemo(() => ({
        selectedPeriodId,
        selectedMonth,
        selectedWeek: timeFrameMode === TIME_FRAME_SINGLE_WEEK ? selectedWeek : null,
        timelineViewRange
    }), [selectedPeriodId, selectedMonth, selectedWeek, timeFrameMode, timelineViewRange]);

    const singleWeekView = useMemo(() => {
        const fromRange = getSingleWeekFromTimelineRange(timelineViewRange);
        if (!fromRange) return null;
        return {
            week: fromRange.week,
            year: fromRange.year || selectedWeek?.year || selectedYearForView
        };
    }, [timelineViewRange, selectedWeek, selectedYearForView]);

    const overlappingPeriodIds = useMemo(() => (
        PeriodManager.getPeriodIdsOverlappingClassSchedulerTimeFrame(
            timePeriods,
            timeFrameSelection,
            yearStructure
        )
    ), [timePeriods, timeFrameSelection, yearStructure]);

    const hasMultipleOverlappingPeriods = useMemo(() => (
        PeriodManager.hasMultiplePeriodsOverlappingClassSchedulerTimeFrame(
            timePeriods,
            timeFrameSelection,
            yearStructure
        )
    ), [timePeriods, timeFrameSelection, yearStructure]);

    const shareWeekRange = useMemo(() => {
        const selectedPeriod = selectedPeriodId != null && selectedPeriodId !== ''
            ? timePeriods.find((period) => String(period.id) === String(selectedPeriodId))
            : null;
        const weeks = selectedPeriod
            ? PeriodManager.getWeeksForPeriod(selectedPeriod, yearStructure)
            : [];

        if (!weeks.length) {
            const year = new Date().getFullYear();
            return { startWeek: 1, startWeekYear: year, endWeek: 1, endWeekYear: year };
        }

        const first = weeks[0];
        const last = weeks[weeks.length - 1];
        return {
            startWeek: first.week,
            startWeekYear: first.year,
            endWeek: last.week,
            endWeekYear: last.year
        };
    }, [selectedPeriodId, timePeriods, yearStructure]);

    const visiblePeriodIds = useMemo(() => {
        if (showOverlappingPeriodEntries) {
            return overlappingPeriodIds;
        }
        const base = selectedPeriodId != null ? [selectedPeriodId] : [];
        if (extraVisiblePeriodId != null && String(extraVisiblePeriodId) !== String(selectedPeriodId)) {
            return [...base, extraVisiblePeriodId];
        }
        return base;
    }, [showOverlappingPeriodEntries, selectedPeriodId, overlappingPeriodIds, extraVisiblePeriodId]);

    const handlePeriodVisibilityToggle = useCallback((periodId) => {
        setExtraVisiblePeriodId((current) => (
            current != null && String(current) === String(periodId) ? null : periodId
        ));
    }, []);

    // Selecting a different period cancels any extra period revealed via the timeline eye toggle.
    useEffect(() => {
        setExtraVisiblePeriodId(null);
    }, [selectedPeriodId]);

    useEffect(() => {
        if (!hasMultipleOverlappingPeriods && showOverlappingPeriodEntries) {
            setShowOverlappingPeriodEntries(false);
        }
    }, [hasMultipleOverlappingPeriods, showOverlappingPeriodEntries]);

    useEffect(() => {
        if (!draftManager) return undefined;

        const handleDraftManagerChange = (state) => {
            if (Array.isArray(state?.drafts)) {
                setDraftsForPeriodLock(state.drafts);
            }
        };

        setDraftsForPeriodLock(Array.isArray(draftManager.drafts) ? draftManager.drafts : []);
        draftManager.addListener(handleDraftManagerChange);

        return () => {
            draftManager.removeListener(handleDraftManagerChange);
        };
    }, [draftManager]);

    useEffect(() => {
        if (!draftManager || !currentDraftName) return;
        if ((draftManager.drafts || []).some(draft => draft?.draft_name === currentDraftName)) return;

        let isMounted = true;
        draftManager.fetchDrafts()
            .then((drafts) => {
                if (isMounted && Array.isArray(drafts)) {
                    setDraftsForPeriodLock(drafts);
                }
            })
            .catch((err) => {
                logger.warn('[ClassSchedulerPage] Failed to refresh drafts for period lock:', err);
            });

        return () => {
            isMounted = false;
        };
    }, [draftManager, currentDraftName]);

    const currentDraftMetadata = useMemo(() => {
        if (!currentDraftName) return null;
        return draftsForPeriodLock.find(draft => draft?.draft_name === currentDraftName) || null;
    }, [currentDraftName, draftsForPeriodLock]);

    const currentDraftPeriodId = currentDraftMetadata?.period_id ?? currentDraftMetadata?.periodId ?? null;

    const allVisibleDrafts = useMemo(
        () => (draftsForPeriodLock || []).filter((draft) => !isHiddenFromVersions(draft)),
        [draftsForPeriodLock]
    );

    const canOpenCopyView = allVisibleDrafts.length >= 2;

    useEffect(() => {
        if (!canOpenCopyView && showCopyView) {
            setShowCopyView(false);
        }
    }, [canOpenCopyView, showCopyView]);

    const [overlappingPeriodLiveEntries, setOverlappingPeriodLiveEntries] = useState([]);

    // Load live schedules from other overlapping periods for cross-period conflict detection.
    useEffect(() => {
        if (!hasMultipleOverlappingPeriods) {
            setOverlappingPeriodLiveEntries([]);
            return undefined;
        }

        const anchorPeriodId = selectedPeriodId ?? currentDraftPeriodId;
        const otherPeriodIds = overlappingPeriodIds.filter(
            (periodId) => anchorPeriodId == null || String(periodId) !== String(anchorPeriodId)
        );

        if (otherPeriodIds.length === 0) {
            setOverlappingPeriodLiveEntries([]);
            return undefined;
        }

        let cancelled = false;

        const loadOverlappingLiveEntries = async () => {
            try {
                const entryGroups = await Promise.all(
                    otherPeriodIds.map((periodId) => assetManager.fetchScheduleEntries(
                        { active_period_id: periodId },
                        '',
                        { forceNetwork: false }
                    ))
                );

                if (cancelled) {
                    return;
                }

                const merged = [];
                const seenIds = new Set();

                entryGroups.flat().forEach((entry) => {
                    if (!entry?.id || seenIds.has(entry.id)) {
                        return;
                    }
                    seenIds.add(entry.id);
                    merged.push(entry);
                });

                logger.debug('[ClassSchedulerPage] Loaded live entries for overlapping periods', {
                    otherPeriodIds,
                    mergedCount: merged.length
                });
                setOverlappingPeriodLiveEntries(merged);
            } catch (error) {
                if (!cancelled) {
                    logger.warn('[ClassSchedulerPage] Failed to load overlapping period live entries:', error);
                    setOverlappingPeriodLiveEntries([]);
                }
            }
        };

        loadOverlappingLiveEntries();

        return () => {
            cancelled = true;
        };
    }, [
        hasMultipleOverlappingPeriods,
        overlappingPeriodIds,
        selectedPeriodId,
        currentDraftPeriodId,
        assetManager
    ]);

    const buildConflictPeerEntries = useCallback((draftEntries) => (
        mergeConflictPeerEntries(
            (draftEntries || []).filter((item) => item?.isScheduled?.()),
            [...overlappingPeriodLiveEntries, ...extraPeriodLiveEntries]
        )
    ), [overlappingPeriodLiveEntries, extraPeriodLiveEntries]);

    // Load live schedule entries for the period revealed via the timeline eye toggle.
    useEffect(() => {
        if (extraVisiblePeriodId == null || extraVisiblePeriodId === '') {
            setExtraPeriodLiveEntries([]);
            return undefined;
        }

        let cancelled = false;

        const loadExtraPeriodEntries = async () => {
            try {
                const entries = await assetManager.fetchScheduleEntries(
                    { active_period_id: extraVisiblePeriodId },
                    '',
                    { forceNetwork: false }
                );

                if (cancelled) {
                    return;
                }

                setExtraPeriodLiveEntries(Array.isArray(entries) ? entries : []);
            } catch (error) {
                if (!cancelled) {
                    logger.warn('[ClassSchedulerPage] Failed to load entries for toggled period:', error);
                    setExtraPeriodLiveEntries([]);
                }
            }
        };

        loadExtraPeriodEntries();

        return () => {
            cancelled = true;
        };
    }, [extraVisiblePeriodId, assetManager]);

    const isPeriodLockedToDraft = currentDraftPeriodId != null && currentDraftPeriodId !== '';
    const draftLockedPeriod = useMemo(() => {
        if (!isPeriodLockedToDraft) return null;
        return timePeriods.find(period => String(period.id) === String(currentDraftPeriodId)) || currentDraftMetadata?.period || null;
    }, [currentDraftMetadata, currentDraftPeriodId, isPeriodLockedToDraft, timePeriods]);
    const periodSelectionDisabledReason = isPeriodLockedToDraft
        ? `Locked to draft period${draftLockedPeriod?.name ? `: ${draftLockedPeriod.name}` : ''}`
        : '';
    const editModalTimeRangeContext = useMemo(() => ({
        selectedPeriodId,
        selectedMonth,
        selectedWeek,
        timePeriods,
        periodSelectionDisabled: isPeriodLockedToDraft,
        periodSelectionDisabledReason
    }), [selectedPeriodId, selectedMonth, selectedWeek, timePeriods, isPeriodLockedToDraft, periodSelectionDisabledReason]);

    const isCurrentDraftActiveForPeriod = useMemo(() => {
        if (!currentDraftMetadata?.id) return false;
        const periodId = currentDraftPeriodId ?? selectedPeriodId;
        if (!periodId) return false;
        const targetPeriod = timePeriods.find((period) => String(period.id) === String(periodId));
        const activeDraftId = targetPeriod?.active_schedule_draft_id ?? targetPeriod?.activeScheduleDraftId;
        return activeDraftId != null && String(activeDraftId) === String(currentDraftMetadata.id);
    }, [currentDraftMetadata, currentDraftPeriodId, selectedPeriodId, timePeriods]);

    const isHistoryDraftOpen = isHistoryDraft(currentDraftMetadata);
    const isLiveViewOnly = isCurrentDraftActiveForPeriod || isHistoryDraftOpen;

    const findWorkingDraftForCurrentLive = useCallback(() => {
        const periodId = currentDraftPeriodId || selectedPeriodId;
        const liveDraftId = currentDraftMetadata?.id;
        return findReusableWorkingDraft({
            drafts: draftsForPeriodLock,
            periodId,
            liveDraftId
        });
    }, [currentDraftMetadata, currentDraftPeriodId, draftsForPeriodLock, selectedPeriodId]);

    const openWorkingDraft = useCallback((draft, { created = false } = {}) => {
        if (!draft?.draft_name) return false;
        handleModeChange(false, draft.draft_name);
        setIsForkLivePromptOpen(false);
        const label = formatEditingDraftLabel(draft);
        setSuccess(created
            ? `Editing ${label}. Live is unchanged until you make this version live.`
            : `Continue editing ${label}. Live is unchanged until you make this version live.`);
        setTimeout(() => setSuccess(null), 5000);
        return true;
    }, [handleModeChange, setSuccess]);

    const handleForkFromLive = useCallback(async () => {
        if (!draftManager || !currentDraftName) return false;

        const existing = findWorkingDraftForCurrentLive();
        if (existing) {
            return openWorkingDraft(existing);
        }

        const period = draftLockedPeriod
            || timePeriods.find((item) => String(item.id) === String(currentDraftPeriodId || selectedPeriodId))
            || null;
        const newName = generateVersionName(period, draftsForPeriodLock);

        setIsForkingLiveDraft(true);
        setIsLoading(true);
        try {
            const result = await draftManager.duplicateDraft(currentDraftName, newName);
            const refreshed = await draftManager.fetchDrafts();
            if (Array.isArray(refreshed)) {
                setDraftsForPeriodLock(refreshed);
            }
            const created = (Array.isArray(refreshed) ? refreshed : []).find((item) => item?.draft_name === newName)
                || {
                    draft_name: newName,
                    display_name: result?.display_name || getDraftTitle(currentDraftMetadata),
                    version_number: result?.version_number
                };
            return openWorkingDraft(created, { created: true });
        } catch (err) {
            setError(`Failed to create a working copy: ${err.message}`);
            return false;
        } finally {
            setIsForkingLiveDraft(false);
            setIsLoading(false);
        }
    }, [
        currentDraftName,
        currentDraftPeriodId,
        draftLockedPeriod,
        draftManager,
        draftsForPeriodLock,
        findWorkingDraftForCurrentLive,
        handleModeChange,
        openWorkingDraft,
        selectedPeriodId,
        setError,
        setIsLoading,
        setSuccess,
        timePeriods
    ]);

    const requestForkFromLive = useCallback(() => {
        if (!isLiveViewOnly) return;
        const existing = findWorkingDraftForCurrentLive();
        if (existing) {
            openWorkingDraft(existing);
            return;
        }
        setIsForkLivePromptOpen(true);
    }, [findWorkingDraftForCurrentLive, isLiveViewOnly, openWorkingDraft]);

    const getScheduleEntryPeriodId = useCallback((entry) => (
        entry?.periodId
        ?? entry?.period_id
        ?? entry?.additionalProps?.periodId
        ?? entry?.additionalProps?.period_id
        ?? null
    ), []);

    const getPeriodDisplayName = useCallback((periodId) => {
        if (periodId == null || periodId === '') return 'another period';
        const period = timePeriods.find((item) => String(item.id) === String(periodId));
        return period?.name || `period ${periodId}`;
    }, [timePeriods]);

    const getCurrentPeriodDisplayName = useCallback(() => {
        const anchorPeriodId = selectedPeriodId ?? currentDraftPeriodId;
        return getPeriodDisplayName(anchorPeriodId);
    }, [currentDraftPeriodId, getPeriodDisplayName, selectedPeriodId]);

    const isSecondaryPeriodEntry = useCallback((entry) => {
        const entryPeriodId = getScheduleEntryPeriodId(entry);
        const anchorPeriodId = selectedPeriodId ?? currentDraftPeriodId;
        if (anchorPeriodId == null || entryPeriodId == null || String(entryPeriodId) === String(anchorPeriodId)) {
            return false;
        }
        if (showOverlappingPeriodEntries) {
            return true;
        }
        return extraVisiblePeriodId != null && String(extraVisiblePeriodId) === String(entryPeriodId);
    }, [currentDraftPeriodId, getScheduleEntryPeriodId, selectedPeriodId, showOverlappingPeriodEntries, extraVisiblePeriodId]);

    const confirmSecondaryPeriodChange = useCallback((entry, actionLabel = 'modify') => {
        if (!isSecondaryPeriodEntry(entry)) return true;

        const entryPeriodName = getPeriodDisplayName(getScheduleEntryPeriodId(entry));
        const currentPeriodName = getCurrentPeriodDisplayName();
        return window.confirm(
            `This entry belongs to ${entryPeriodName}, not the currently selected period (${currentPeriodName}).\n\n` +
            `If you continue, this will ${actionLabel} the entry in ${entryPeriodName} only and will not change ${currentPeriodName}.\n\n` +
            'Continue?'
        );
    }, [getCurrentPeriodDisplayName, getPeriodDisplayName, getScheduleEntryPeriodId, isSecondaryPeriodEntry]);

    const persistSecondaryPeriodEntry = useCallback(async (updatedEntry, originalEntry, actionLabel = 'modify') => {
        const entryForPrompt = originalEntry || updatedEntry;
        if (!confirmSecondaryPeriodChange(entryForPrompt, actionLabel)) {
            return null;
        }

        const entryToSave = updatedEntry instanceof ScheduleEntry
            ? updatedEntry
            : new ScheduleEntry(updatedEntry);
        const originalPeriodId = getScheduleEntryPeriodId(entryForPrompt);
        if (originalPeriodId != null && originalPeriodId !== '') {
            entryToSave.periodId = originalPeriodId;
        }
        const savedEntry = await assetManager.updateScheduleEntry(entryToSave.id, entryToSave);

        setOverlappingPeriodLiveEntries(prevEntries => prevEntries.map(entry =>
            String(entry.id) === String(savedEntry.id) ? savedEntry : entry
        ));

        const periodName = getPeriodDisplayName(getScheduleEntryPeriodId(savedEntry));
        setSuccess(`Updated entry in ${periodName}`);
        setTimeout(() => setSuccess(null), 3000);

        return savedEntry;
    }, [assetManager, confirmSecondaryPeriodChange, getPeriodDisplayName, getScheduleEntryPeriodId, setSuccess]);

    useEffect(() => {
        if (!isPeriodLockedToDraft) return;
        if (String(selectedPeriodId) === String(currentDraftPeriodId)) return;

        const matchingPeriod = timePeriods.find(period => String(period.id) === String(currentDraftPeriodId));
        setSelectedPeriodId(matchingPeriod?.id ?? currentDraftPeriodId);
    }, [currentDraftPeriodId, isPeriodLockedToDraft, selectedPeriodId, setSelectedPeriodId, timePeriods]);

    // Draft initialization moved into useClassSchedulerState

    // Draft state listening handled within useClassSchedulerState

    // Cleanup on unmount - clear localStorage if needed
    useEffect(() => {
        return () => {
            // Optional: Clear draft state on unmount if in a temporary state
            // For now, we'll keep the state persistent across sessions
            logger.debug('[ClassSchedulerPage] Component unmounting - draft state preserved');
        };
    }, []);

    // Undo/Redo keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Only handle shortcuts when not typing in an input
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
                return;
            }

            if (showCopyView) {
                return;
            }

            // Ctrl+Z or Cmd+Z for undo
            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                if (canUndo()) {
                    const success = handleUndo();
                    if (success) {
                        setSuccess('Undid last action');
                        setTimeout(() => setSuccess(null), 2000);
                    }
                }
            }
            // Ctrl+Y, Ctrl+Shift+Z, or Cmd+Shift+Z for redo
            else if ((event.ctrlKey && event.key === 'y') ||
                (event.ctrlKey && event.shiftKey && event.key === 'Z') ||
                (event.metaKey && event.shiftKey && event.key === 'Z')) {
                event.preventDefault();
                if (canRedo()) {
                    const success = handleRedo();
                    if (success) {
                        setSuccess('Redid last action');
                        setTimeout(() => setSuccess(null), 2000);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleUndo, handleRedo, canUndo, canRedo, setSuccess, showCopyView]);

    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
    // Ref to hold the save-to-API function to avoid initialization order issues
    const saveChangesToAPIRef = useRef(null);
    const lastEditedItemIdRef = useRef(null);
    const updatedItemsForAutoSaveRef = useRef(null);
    const scheduledInstancesRef = useRef(scheduledInstances);
    useLayoutEffect(() => {
        scheduledInstancesRef.current = scheduledInstances;
    }, [scheduledInstances]);

    // Conflict detection state
    const [currentConflicts, setCurrentConflicts] = useState([]);
    const [isConflictListVisible, setIsConflictListVisible] = useState(false);
    const [conflictCalculationTimeoutId, setConflictCalculationTimeoutId] = useState(null);
    const [isCalculatingConflicts, setIsCalculatingConflicts] = useState(false);

    // Save animation state
    const [isSaveAnimationActive, setIsSaveAnimationActive] = useState(false);

    // Track locally deleted items to prevent them from being re-added by filtering effects
    const [locallyDeletedItemIds, setLocallyDeletedItemIds] = useState(new Set());

    // Track items for scrolling and highlighting (new saves or conflict navigation)
    const [highlightedItemIds, setHighlightedItemIds] = useState([]);

    const [entryTypeFilters, setEntryTypeFilters] = useState({
        class: true,
        meeting: true,
        event: true
    });
    const [roomVisibilityFilter, setRoomVisibilityFilter] = useState('all');

    // Drawer state for unscheduled courses container
    const [isUnscheduledDrawerOpen, setIsUnscheduledDrawerOpen] = useState(false);

    // Drawer state for events container
    const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false);

    // Drawer state for meetings container
    const [isMeetingDrawerOpen, setIsMeetingDrawerOpen] = useState(false);

    const currentDate = useMemo(() => new Date(), []);

    const isAdmin = user?.role === 'Admin';

    // Scroll tracking state for fixed day header (will be initialized after activeDays is loaded)
    const [currentVisibleDay, setCurrentVisibleDay] = useState('');
    const [previousDay, setPreviousDay] = useState('');
    const scrollContainerRef = useRef(null);
    const dayHeaderRefs = useRef({});

    // Track page header hide/show based on inner scroll
    const setupHeaderHiddenRef = useRef(false);
    const lastInnerScrollTopRef = useRef(0);
    const maxDownInnerScrollTopRef = useRef(0);

    // Get unique rooms from real data instead of mock data
    const allRoomsForGrid = useMemo(() => {
        if (rooms.length === 0) return [];
        return rooms; // Pass room objects directly instead of extracting names
    }, [rooms]);

    const filteredRoomsForGrid = useMemo(
        () => filterRoomsByVisibility(allRoomsForGrid, roomVisibilityFilter),
        [allRoomsForGrid, roomVisibilityFilter]
    );

    useEffect(() => {
        if (!selectedRoomForView || filteredRoomsForGrid.length === 0) return;
        const selectedName = typeof selectedRoomForView === 'object'
            ? selectedRoomForView.name
            : selectedRoomForView;
        const isStillInList = filteredRoomsForGrid.some((room) => room.name === selectedName);
        if (!isStillInList) {
            setSelectedRoomForView(filteredRoomsForGrid[0]);
        }
    }, [filteredRoomsForGrid, selectedRoomForView, setSelectedRoomForView]);

    // Track unsaved changes (aligned with Save Draft / ID-set tracking)
    const pendingChangeIdCounts = typeof getChangeIdCounts === 'function'
        ? getChangeIdCounts()
        : { created: 0, modified: 0, deleted: 0 };
    const hasUnsavedChanges = (pendingChangeIdCounts.created + pendingChangeIdCounts.modified + pendingChangeIdCounts.deleted) > 0;
    const [pendingPeriodChange, setPendingPeriodChange] = useState(null);
    const [isUnsavedPeriodPromptSaving, setIsUnsavedPeriodPromptSaving] = useState(false);
    const [pendingDraftLiveChange, setPendingDraftLiveChange] = useState(null);
    const [isDraftLiveConfirming, setIsDraftLiveConfirming] = useState(false);

    // Warn on page unload if there are unsaved changes
    useEffect(() => {
        const beforeUnloadHandler = (e) => {
            if (!hasUnsavedChanges) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
    }, [hasUnsavedChanges]);

    // Block in-app navigation when there are unsaved changes (without data router)
    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const shouldIntercept = (anchor) => {
            if (!anchor || !anchor.href) return false;
            try {
                const url = new URL(anchor.href, window.location.origin);
                return url.origin === window.location.origin && url.pathname !== window.location.pathname;
            } catch (_) { return false; }
        };

        const onClick = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            let el = e.target;
            while (el && el !== document.body) {
                if (el.tagName === 'A' && shouldIntercept(el)) {
                    const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
                    if (!ok) e.preventDefault();
                    return;
                }
                el = el.parentElement;
            }
        };

        const onPopState = () => {
            const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
            if (!ok) {
                window.history.pushState(null, '', window.location.href);
            }
        };

        document.addEventListener('click', onClick, true);
        window.addEventListener('popstate', onPopState);
        return () => {
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('popstate', onPopState);
        };
    }, [hasUnsavedChanges]);

    // Assets fetching moved into useClassSchedulerState

    // Initialize the drag-to-create hook

    // Helper to set the item and open the modal with autosave behavior
    const openModalWithAutosave = useCallback((item) => {
        // Try to infer a concrete week context when possible to surface exception UI in the editor
        const determineWeekContext = () => {
            if (selectedWeek && typeof selectedWeek.week === 'number') {
                return { week: selectedWeek.week, year: selectedWeek.year };
            }
            if (timelineViewRange && timelineViewRange.startWeek && timelineViewRange.endWeek && timelineViewRange.startWeek === timelineViewRange.endWeek) {
                return { week: timelineViewRange.startWeek, year: selectedYearForView };
            }
            return null;
        };

        const maybeAttachExceptionContext = (entry) => {
            const ctx = determineWeekContext();
            if (!ctx) return entry; // No single-week context available

            try {
                const root = (scheduledInstancesRef.current || []).find(it => String(it.id) === String(entry.id));
                if (!root || typeof root.getEntryForWeek !== 'function') return entry;
                const weekEntry = root.getEntryForWeek(ctx.week, ctx.year);
                if (!weekEntry) return entry; // cancelled this week
                // If weekEntry has a different id, it's an exception occurrence
                if (weekEntry.id !== root.id) {
                    try {
                        const cloned = typeof entry.clone === 'function'
                            ? entry.clone()
                            : new ScheduleEntry(typeof entry.toObject === 'function' ? entry.toObject() : entry);
                        try { cloned._exceptionContext = { originalId: root.id, week: ctx.week, year: ctx.year }; } catch (_) { }
                        return cloned;
                    } catch (_) {
                        try { entry._exceptionContext = { originalId: root.id, week: ctx.week, year: ctx.year }; } catch (_) { }
                        return entry;
                    }
                }
                return entry;
            } catch (_) {
                return entry;
            }
        };

        const enrichedItem = maybeAttachExceptionContext(item);
        setSelectedScheduleItem(enrichedItem);
        setIsScheduleModalOpen(true);
    }, [setIsScheduleModalOpen, setSelectedScheduleItem, selectedWeek, selectedYearForView, timelineViewRange]);

    const derivedTimelineContext = useMemo(() => deriveScheduleTimelineContext({
        selectedWeek,
        timelineViewRange,
        selectedYearForView,
        selectedPeriod: selectedPeriodId ? timePeriods.find(p => String(p.id) === String(selectedPeriodId)) : null,
        fallbackDate: currentDate
    }), [selectedWeek, timelineViewRange, selectedYearForView, selectedPeriodId, timePeriods, currentDate]);

    const {
        dragToCreateData,
        handleDragToCreateStart,
        handleDragToCreateMove,
        handleDragToCreateEnd,
        handleDragToCreateCancel
    } = useDragToCreate(user?.role === 'Admin', {
        onModalCreate: true,
        setSelectedScheduleItem,
        setIsScheduleModalOpen: setIsScheduleModalOpen,
        currentYear: derivedTimelineContext.year,
        currentDate: derivedTimelineContext.date,
        selectedPeriodId,
        rooms: rooms, // Pass rooms array for room name lookup
        timelineContext: derivedTimelineContext,
        // NEW: Provide context-specific week range for ClassSchedulerPage
        getContextWeekRange: () => {
            // Priority 1: Use timeline view range if available (user-selected range)
            if (timelineViewRange && timelineViewRange.startWeek && timelineViewRange.endWeek) {
                logger.debug('[ClassSchedulerPage] Using timeline view range for drag-to-create:',
                    timelineViewRange.startWeek, '-', timelineViewRange.endWeek);

                // Format as week series string
                if (timelineViewRange.startWeek === timelineViewRange.endWeek) {
                    return timelineViewRange.startWeek.toString();
                } else {
                    return `${timelineViewRange.startWeek}-${timelineViewRange.endWeek}`;
                }
            }

            // Priority 2: Use selected period's week range
            if (selectedPeriodId && timePeriods.length > 0) {
                const selectedPeriod = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
                if (selectedPeriod && selectedPeriod.start_week && selectedPeriod.end_week) {
                    logger.debug('[ClassSchedulerPage] Using selected period range for drag-to-create:',
                        selectedPeriod.start_week, '-', selectedPeriod.end_week);

                    // Format as week series string
                    if (selectedPeriod.start_week === selectedPeriod.end_week) {
                        return selectedPeriod.start_week.toString();
                    } else {
                        return `${selectedPeriod.start_week}-${selectedPeriod.end_week}`;
                    }
                }
            }

            // Priority 3: Fallback to full year (will be handled by default in useDragToCreate)
            logger.debug('[ClassSchedulerPage] No specific range available, using default');
            return null;
        }
    });

    // Get active days from AssetManager
    const { activeDays } = useActiveDays();

    // Initialize currentVisibleDay when activeDays is loaded
    useEffect(() => {
        if (activeDays.length > 0 && !currentVisibleDay) {
            setCurrentVisibleDay(activeDays[0]);
            setPreviousDay(activeDays[0]);
        }
    }, [activeDays, currentVisibleDay]);

    // Function to determine animation direction for day header transitions
    const getAnimationDirection = useCallback(() => {
        if (!activeDays || activeDays.length === 0 || !currentVisibleDay || !previousDay) return 'slideUp';
        const currentIndex = activeDays.indexOf(currentVisibleDay);
        const previousIndex = activeDays.indexOf(previousDay);
        // If either index is -1 (not found), default to slideUp
        if (currentIndex === -1 || previousIndex === -1) return 'slideUp';
        return currentIndex > previousIndex ? 'slideUp' : 'slideDown';
    }, [currentVisibleDay, previousDay, activeDays]);

    // --- Initialize the custom hook for drag and drop ---
    // Pass addLogEntry to enable change tracking in the drag and drop system
    // Use real data instead of mock data
    const {
        draggedItemInfo,
        availabilityOverlayData,
        handleGlobalDragStart,
        handleGlobalDragEnd,
        handleItemDrop: handleCalendarItemDrop, // Renaming to distinguish from potential sidebar drop
    } = useScheduleDragAndDrop(scheduledInstances, setScheduledInstances, user, addLogEntry, teachers, groups, subjects, rooms, {
        autoApplyToAllWeeks: true, // ClassSchedulerPage should automatically apply changes to all weeks
        activeDays,
        timelineContext: derivedTimelineContext,
        conflictPeerEntries: overlappingPeriodLiveEntries
    });

    // Memoize expensive week series parsing to avoid repeated calculations
    const memoizedWeekSeriesParsing = useMemo(() => {
        const cache = new Map();
        return (weekSeries) => {
            if (!cache.has(weekSeries)) {
                cache.set(weekSeries, parseWeekSeries(weekSeries));
            }
            return cache.get(weekSeries);
        };
    }, []);

    // Simple flag to track if we have set the initial baseline
    // Provided by useClassSchedulerState

    // Additional flag to track if conflicts have been calculated for initial data
    // hasInitialConflictsCalculated provided by useClassSchedulerState

    // MODIFIED: Effect to compute filteredScheduledInstances based on selected period (+ optional week/month)
    // IMPORTANT: Do NOT overwrite scheduledInstances (source of truth). Only set filteredScheduledInstances.
    const filterEntriesForPeriodAndTime = useCallback((baseList, options = {}) => {
        const periodResolutionStructure = yearStructure
            ? { ...yearStructure, periods: timePeriods }
            : { periods: timePeriods };

        const list = Array.isArray(baseList) ? baseList : [];
        const skipPeriodScope = options.skipPeriodScope === true;

        const visiblePeriodObjects = (showOverlappingPeriodEntries ? overlappingPeriodIds : [
            ...(selectedPeriodId != null ? [selectedPeriodId] : []),
            ...(extraVisiblePeriodId != null && String(extraVisiblePeriodId) !== String(selectedPeriodId) ? [extraVisiblePeriodId] : [])
        ])
            .map((periodId) => timePeriods.find(p => String(p.id) === String(periodId)))
            .filter(Boolean);

        const primaryPeriodObj = selectedPeriodId && timePeriods.length > 0
            ? timePeriods.find(p => String(p.id) === String(selectedPeriodId))
            : (visiblePeriodObjects[0] ?? null);
        const scopeYear = primaryPeriodObj?.year ?? selectedYearForView;

        const getEntryYearForTimeline = (item) => {
            if (item && typeof item.resolvePeriodScope === 'function') {
                const scope = item.resolvePeriodScope(periodResolutionStructure);
                if (scope?.academicYearStart != null) return scope.academicYearStart;
            }
            return scopeYear;
        };

        const matchesPeriod = (item, periodObj) => {
            if (!item || typeof item.isScheduled !== 'function' || !item.isScheduled()) {
                return true;
            }
            return PeriodManager.scheduleEntryBelongsToPeriod(
                item,
                periodObj,
                periodResolutionStructure
            );
        };

        const matchesVisiblePeriods = (item) => {
            if (!visiblePeriodObjects.length) {
                return matchesPeriod(item, primaryPeriodObj);
            }
            return visiblePeriodObjects.some((periodObj) => matchesPeriod(item, periodObj));
        };

        const periodScopedList = skipPeriodScope ? list : list.filter(matchesVisiblePeriods);

        const getEntryWeekSeries = (entry) => entry?.weekSeries || entry?.weekSeries;

        const singleWeekBounds = getSingleWeekFromTimelineRange(timelineViewRange);
        const weekNumber = singleWeekBounds?.week ?? null;
        const weekYear = singleWeekBounds?.year || selectedWeek?.year || scopeYear;

        const applyWeekExceptionsIfSingleWeekBounds = (entries) => {
            if (weekNumber == null) return entries;

            const withExceptionsApplied = entries
                .map(item => {
                    if (!item || typeof item.isScheduled !== 'function' || !item.isScheduled()) {
                        return item;
                    }
                    const getForWeek = typeof item.getEntryForWeek === 'function'
                        ? item.getEntryForWeek
                        : (typeof item.getEntryForWeek === 'function' ? item.getEntryForWeek : null);
                    if (getForWeek) {
                        const result = getForWeek.call(item, weekNumber, weekYear);
                        if (!result) return null;
                        if (result.id !== item.id) {
                            try {
                                const resObj = typeof result.toObject === 'function' ? result.toObject() : result;
                                const newInstance = new ScheduleEntry({
                                    ...resObj,
                                    id: item.id
                                });
                                try { newInstance._exceptionContext = { originalId: item.id, week: weekNumber, year: weekYear }; } catch (_) { }
                                return newInstance;
                            } catch (e) {
                                try {
                                    result.id = item.id;
                                    result._exceptionContext = { originalId: item.id, week: weekNumber, year: weekYear };
                                } catch (_) { }
                                return result;
                            }
                        }
                        return result;
                    }
                    return item;
                })
                .filter(Boolean);

            return withExceptionsApplied.filter(item => {
                if (!item || typeof item.isScheduled !== 'function' || !item.isScheduled()) return true;
                if (typeof item.isActiveForWeekSync === 'function') {
                    return item.isActiveForWeekSync(weekNumber, weekYear);
                }
                if (typeof item.isActiveForWeekSync === 'function') {
                    return item.isActiveForWeekSync(weekNumber, weekYear);
                }
                const series = getEntryWeekSeries(item);
                if (series) {
                    const entryWeekNumbers = memoizedWeekSeriesParsing(series);
                    return entryWeekNumbers.includes(weekNumber);
                }
                return true;
            });
        };

        const applyTimelineRangeIfAny = (entries) => {
            if (!timelineViewRange) return entries;
            return entries.filter(entry => {
                const coords = PeriodManager.getScheduleEntryWeekCoordinates(entry, periodResolutionStructure);
                if (Array.isArray(coords) && coords.length) {
                    return coords.some(({ week, year }) => timelineViewRange.isWeekInRange(week, year));
                }
                const entryYear = getEntryYearForTimeline(entry);
                const series = getEntryWeekSeries(entry);
                if (!series || entryYear == null) {
                    return false;
                }
                const entryWeekNumbers = memoizedWeekSeriesParsing(series);
                if (!entryWeekNumbers.length) return false;
                return entryWeekNumbers.some(weekNum => timelineViewRange.isWeekInRange(weekNum, entryYear));
            });
        };

        return applyTimelineRangeIfAny(applyWeekExceptionsIfSingleWeekBounds(periodScopedList));
    }, [
        selectedPeriodId,
        selectedWeek,
        selectedYearForView,
        yearStructure,
        timePeriods,
        memoizedWeekSeriesParsing,
        timelineViewRange,
        showOverlappingPeriodEntries,
        overlappingPeriodIds,
        extraVisiblePeriodId
    ]);

    // useLayoutEffect: keep filtered list in sync before paint when scheduledInstances updates (fetch does not set filtered in the hook).
    useLayoutEffect(() => {
        console.log('*** MAIN FILTERING EFFECT (VIEW-ONLY) TRIGGERED ***', {
            timestamp: new Date().toISOString(),
            isFetchingDefinitions,
            selectedPeriodId,
            selectedMonth: selectedMonth?.month,
            selectedWeek: selectedWeek?.week,
            scheduledInstancesLen: scheduledInstancesRef.current?.length || 0
        });

        let baseList = Array.isArray(scheduledInstancesRef.current)
            ? [...scheduledInstancesRef.current]
            : [];

        if (showOverlappingPeriodEntries && overlappingPeriodLiveEntries.length > 0) {
            const existingIds = new Set(baseList.map((item) => item?.id).filter(Boolean));
            overlappingPeriodLiveEntries.forEach((entry) => {
                if (entry?.id && !existingIds.has(entry.id)) {
                    baseList.push(entry);
                    existingIds.add(entry.id);
                }
            });
        }

        if (extraPeriodLiveEntries.length > 0) {
            const existingIds = new Set(baseList.map((item) => item?.id).filter(Boolean));
            extraPeriodLiveEntries.forEach((entry) => {
                if (entry?.id && !existingIds.has(entry.id)) {
                    baseList.push(entry);
                    existingIds.add(entry.id);
                }
            });
        }

        setFilteredScheduledInstances(filterEntriesForPeriodAndTime(baseList));
    }, [filterEntriesForPeriodAndTime, scheduledInstances, showOverlappingPeriodEntries, overlappingPeriodLiveEntries, extraPeriodLiveEntries, isFetchingDefinitions, selectedPeriodId, selectedMonth, selectedWeek]);

    // Net-change calculation is now managed centrally via the controller when needed

    // --- Conflict Calculation Logic ---
    // Use a ref to track the last calculated items to prevent unnecessary recalculations
    const lastConflictCalculationRef = useRef(null);

    const calculateAllConflicts = useCallback(async (itemsToCalculate) => {
        if (!isAdmin || isCalculatingConflicts) return;

        const safeScheduleItems = Array.isArray(itemsToCalculate) ? itemsToCalculate : [];
        const scheduledItems = safeScheduleItems.filter(item => item.isScheduled());
        const resolvedPeerEntries = buildConflictPeerEntries(scheduledItems);

        if (scheduledItems.length === 0) {
            setCurrentConflicts([]);

            // If no scheduled items, just set the flag that conflicts have been calculated
            // Baseline is now set immediately when data loads
            if (!hasInitialConflictsCalculated) {
                logger.debug('[ClassSchedulerPage] No scheduled items for conflict calculation, marking conflicts as calculated');
                setHasInitialConflictsCalculated(true);
            }

            return;
        }

        // Check if we've already calculated conflicts for these exact items
        const itemsSignature = [
            scheduledItems.map(item => `${item.id}-${item.timeStart}-${item.timeEnd}-${item.room}`).join('|'),
            resolvedPeerEntries
                .filter((item) => !scheduledItems.some((draftItem) => draftItem.id === item.id))
                .map(item => `${item.id}-${item.timeStart}-${item.timeEnd}-${item.room}`)
                .join('|')
        ].join('::');
        if (lastConflictCalculationRef.current === itemsSignature) {
            return; // Skip if already calculated
        }

        setIsCalculatingConflicts(true);
        logger.debug("[ClassSchedulerPage] Recalculating all conflicts using ConflictManager...");

        try {
            // Use ConflictManager to recalculate conflicts for all entries
            const updatedEntries = await conflictManager.calculateConflictsForEntries(
                scheduledItems,
                teachers,
                groups,
                subjects,
                { peerEntries: resolvedPeerEntries }
            );

            // Extract conflicts for the conflict list display
            const conflicts = [];
            updatedEntries.forEach(entry => {
                const overallStatus = entry.getOverallConflictStatus();
                if (overallStatus === 'red' || overallStatus === 'orange') {
                    // Collect all conflict reasons from all weeks with week information
                    const allReasonsWithWeeks = [];

                    if (entry.conflictsByWeek && entry.conflictsByWeek.size > 0) {
                        entry.conflictsByWeek.forEach((weekConflict, weekKey) => {
                            if (weekConflict.reasons && weekConflict.reasons.length > 0) {
                                // Add week information to each reason if not already present
                                const reasonsWithWeek = weekConflict.reasons.map(reason => {
                                    const hasWeekInfo = /week \d+/i.test(reason.reason) ||
                                        /\(week \d+\)/i.test(reason.reason);

                                    const decoratedReason = decorateConflictReasonForDisplay(
                                        {
                                            ...reason,
                                            week: reason.week ?? weekConflict.week,
                                            year: reason.year ?? weekConflict.year,
                                            reason: hasWeekInfo
                                                ? reason.reason
                                                : `${reason.reason} (week ${weekConflict.week})`
                                        },
                                        getPeriodDisplayName
                                    );

                                    return decoratedReason;
                                });
                                allReasonsWithWeeks.push(...reasonsWithWeek);
                            }
                        });
                    }

                    // If no conflicts found, add a generic reason
                    const finalReasons = allReasonsWithWeeks.length > 0
                        ? allReasonsWithWeeks
                        : [{ reason: `Status: ${overallStatus}`, severity: overallStatus }];

                    conflicts.push({
                        itemId: entry.id,
                        itemName: entry.displayName,
                        day: entry.day,
                        time: entry.timeRangeDisplay(),
                        room: entry.room,
                        reasons: finalReasons
                    });
                }
            });

            logger.debug("[ClassSchedulerPage] Conflicts found:", conflicts.length);
            setCurrentConflicts(conflicts);

            // Update the scheduled instances with new conflict information
            setScheduledInstances(prevItems => {
                console.log('*** CONFLICT CALCULATION - UPDATING SCHEDULED INSTANCES ***', {
                    prevItemsLength: prevItems.length,
                    updatedEntriesLength: updatedEntries.length,
                    itemsSignature
                });

                // Create a map of updated entries for efficient lookup
                const updatedEntriesMap = new Map(updatedEntries.map(entry => [entry.id, entry]));

                // Update items with new conflict information
                const updatedItems = prevItems.map(item => {
                    if (item.isScheduled()) {
                        const updatedEntry = updatedEntriesMap.get(item.id);
                        if (updatedEntry) {
                            return updatedEntry;
                        }
                    }
                    return item;
                });

                console.log('*** CONFLICT CALCULATION - UPDATED ITEMS RESULT ***', {
                    updatedItemsLength: updatedItems.length,
                    sampleIds: updatedItems.slice(0, 5).map(item => item?.id)
                });

                lastConflictCalculationRef.current = itemsSignature;

                // Baseline is now set immediately when data loads, not here
                console.log('[ClassSchedulerPage] *** CONFLICT CALCULATION COMPLETE - BASELINE ALREADY SET ***', {
                    isBaselineSet,
                    hasInitialConflictsCalculated,
                    updatedItemsLength: updatedItems.length
                });

                // Set the flag that conflicts have been calculated
                setHasInitialConflictsCalculated(true);

                return updatedItems;
            });

        } catch (error) {
            console.error('[ClassSchedulerPage] Error calculating conflicts:', error);
            setCurrentConflicts([]);
        } finally {
            setIsCalculatingConflicts(false);
        }
    }, [isAdmin, isCalculatingConflicts, teachers, groups, subjects, isBaselineSet, hasInitialConflictsCalculated, courseDefinitions, isFetchingDefinitions, getPeriodDisplayName, buildConflictPeerEntries]);

    // Debounced effect for conflict calculation
    useEffect(() => {
        if (!isAdmin) {
            setCurrentConflicts([]);
            return;
        }

        // Clear any existing timeout
        if (conflictCalculationTimeoutId) {
            clearTimeout(conflictCalculationTimeoutId);
        }

        // Set a new timeout to debounce the calculation
        const timeoutId = setTimeout(() => {
            calculateAllConflicts(scheduledInstances);
        }, 300); // 300ms debounce

        setConflictCalculationTimeoutId(timeoutId);

        // Cleanup function
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [scheduledInstances, overlappingPeriodLiveEntries, extraPeriodLiveEntries, isAdmin, calculateAllConflicts]);

    const toggleConflictList = () => setIsConflictListVisible(prev => !prev);

    // Timeline range is handled inside the main view-only filtering effect.
    const handleTimelineRangeExternalChange = useCallback((rangeData) => {
        setTimelineViewRange(rangeData);
        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    }, [hasUnsavedChanges, setSuccess]);

    // Manual timeframe selection via the draggable delimiters on the period timeline.
    const handleTimelineRangeManualChange = useCallback(({ startWeek, startWeekYear, endWeek, endWeekYear }) => {
        setTimeFrameMode(null);
        setSelectedWeek(null);
        setSelectedMonth(null);
        setTimelineViewRange(createTimelineViewRange({
            startWeek,
            startWeekYear,
            endWeek,
            endWeekYear
        }));
    }, [setSelectedMonth, setSelectedWeek, setTimelineViewRange]);

    const handleTimeFrameRangeChange = useCallback((range) => {
        setTimeFrameMode(range);
        setSelectedMonth(null);
        if (range === TIME_FRAME_WHOLE_PERIOD) {
            const period = selectedPeriodId != null
                ? timePeriods.find((p) => String(p.id) === String(selectedPeriodId))
                : null;
            setTimelineViewRange(buildPeriodTimelineRange(period));
        }
    }, [buildPeriodTimelineRange, selectedPeriodId, setSelectedMonth, setTimelineViewRange, timePeriods]);

    useEffect(() => {
        if (timeFrameMode !== TIME_FRAME_WHOLE_PERIOD) return;
        const period = selectedPeriodId != null
            ? timePeriods.find((p) => String(p.id) === String(selectedPeriodId))
            : null;
        setTimelineViewRange(buildPeriodTimelineRange(period));
    }, [timeFrameMode, selectedPeriodId, timePeriods, buildPeriodTimelineRange, setTimelineViewRange]);

    // View filtering is centralized in the main effect; no-op here to avoid overrides.
    useEffect(() => {
        // Intentionally left blank
    }, [scheduledInstances, timelineViewRange]);

    // Apply free-text filtering on top of timeline-based filtering
    const textFilteredScheduledInstances = useMemo(() => {
        const query = (textFilterQuery || '').trim().toLowerCase();
        if (!query) return filteredScheduledInstances;

        const stringIncludes = (value) => {
            if (!value || typeof value !== 'string') return false;
            return value.toLowerCase().includes(query);
        };

        return filteredScheduledInstances.filter(entry => {
            // Collect candidate strings
            const hostCandidates = [
                entry.teacher,
                entry?.additionalProps?.host,
                entry?.additionalProps?.eventHost,
                entry?.additionalProps?.event_host
            ];

            const groupNames = Array.isArray(entry.groups)
                ? entry.groups.map(g => {
                    if (g && typeof g === 'object') {
                        return String(g.name || g.group_code || '');
                    }
                    return g != null ? String(g) : '';
                }).filter(Boolean)
                : [];

            // Multi-teacher support: include formatted teacher list and individual names
            const teachersFormatted = typeof entry.teachersFormatted === 'string' ? entry.teachersFormatted : '';
            const teacherNamesMulti = (() => {
                // Prefer model helper when available to avoid duplicating logic
                if (typeof entry?.hasTeacher === 'function') {
                    // We still want candidate strings for text search; derive from arrays
                }
                const raw = (entry && (entry.teachers || entry.teachersArray)) || [];
                if (!Array.isArray(raw)) return [];
                return raw.map(t => {
                    if (t && typeof t === 'object') return String(t.name || '');
                    return t != null ? String(t) : '';
                }).filter(Boolean);
            })();

            const candidates = [
                entry.subject,
                entry.course,
                entry.title,
                entry.eventName,
                entry.description,
                ...hostCandidates,
                ...groupNames,
                teachersFormatted,
                ...teacherNamesMulti
            ].filter(v => typeof v === 'string' && v.trim() !== '');

            // If all relevant fields are empty, do not exclude this entry
            if (candidates.length === 0) return true;

            // Match if any candidate contains the query
            return candidates.some(stringIncludes);
        });
    }, [filteredScheduledInstances, textFilterQuery]);

    const entryTypeFilteredScheduledInstances = useMemo(() => {
        const enabledTypes = Object.entries(entryTypeFilters || {})
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);
        const totalTypes = Object.keys(entryTypeFilters || {}).length;

        if (totalTypes === 0 || enabledTypes.length === totalTypes) {
            return textFilteredScheduledInstances;
        }

        return textFilteredScheduledInstances.filter(entry => {
            const rawType = (entry && typeof entry.type === 'string' ? entry.type : '').toLowerCase();

            const normalizedKey = rawType === 'class'
                ? 'class'
                : rawType === 'meeting'
                    ? 'meeting'
                    : rawType === 'event'
                        ? 'event'
                        : null;

            if (!normalizedKey) {
                // If the type is unknown, keep it visible
                return true;
            }

            return !!entryTypeFilters?.[normalizedKey];
        });
    }, [textFilteredScheduledInstances, entryTypeFilters]);

    const getViewFilteredEntries = useCallback((entries, options = {}) => {
        const periodFiltered = filterEntriesForPeriodAndTime(entries, options);
        const query = (textFilterQuery || '').trim().toLowerCase();
        let result = periodFiltered;

        if (query) {
            const stringIncludes = (value) => {
                if (!value || typeof value !== 'string') return false;
                return value.toLowerCase().includes(query);
            };

            result = result.filter(entry => {
                const hostCandidates = [
                    entry.teacher,
                    entry?.additionalProps?.host,
                    entry?.additionalProps?.eventHost,
                    entry?.additionalProps?.event_host
                ];

                const groupNames = Array.isArray(entry.groups)
                    ? entry.groups.map(g => {
                        if (g && typeof g === 'object') {
                            return String(g.name || g.group_code || '');
                        }
                        return g != null ? String(g) : '';
                    }).filter(Boolean)
                    : [];

                const teachersFormatted = typeof entry.teachersFormatted === 'string' ? entry.teachersFormatted : '';
                const teacherNamesMulti = (() => {
                    const raw = (entry && (entry.teachers || entry.teachersArray)) || [];
                    if (!Array.isArray(raw)) return [];
                    return raw.map(t => {
                        if (t && typeof t === 'object') return String(t.name || '');
                        return t != null ? String(t) : '';
                    }).filter(Boolean);
                })();

                const candidates = [
                    entry.subject,
                    entry.course,
                    entry.title,
                    entry.eventName,
                    entry.description,
                    ...hostCandidates,
                    ...groupNames,
                    teachersFormatted,
                    ...teacherNamesMulti,
                    entry.room,
                    entry.day,
                    entry.timeStart,
                    entry.timeEnd,
                    entry.weekSeries
                ];

                return candidates.some(stringIncludes);
            });
        }

        const enabledTypes = Object.entries(entryTypeFilters || {})
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);
        const totalTypes = Object.keys(entryTypeFilters || {}).length;

        if (totalTypes > 0 && enabledTypes.length < totalTypes) {
            result = result.filter(entry => {
                const rawType = (entry && typeof entry.type === 'string' ? entry.type : '').toLowerCase();
                const normalizedKey = rawType === 'class'
                    ? 'class'
                    : rawType === 'meeting'
                        ? 'meeting'
                        : rawType === 'event'
                            ? 'event'
                            : null;

                if (!normalizedKey) return true;
                return !!entryTypeFilters?.[normalizedKey];
            });
        }

        return result;
    }, [filterEntriesForPeriodAndTime, textFilterQuery, entryTypeFilters]);

    // MODIFIED: unscheduledCourses are those from text-filtered working set that are not scheduled
    const unscheduledCourses = useMemo(() => {
        return entryTypeFilteredScheduledInstances.filter(item => !item.isScheduled());
    }, [entryTypeFilteredScheduledInstances]);

    // --- Specific Drop Handler for ClassSchedulerPage ---
    // This handler will decide if it's a new item from the sidebar or an existing item being moved.
    const handleActualItemDrop = useCallback(async (droppedItemId, targetRoomId, targetDay, newStartTimeString) => {
        // Resolve room ID to room object for logging and template creation
        const targetRoom = rooms.find(room => room.id === targetRoomId);
        const targetRoomName = targetRoom?.name || `Room ID ${targetRoomId}`;

        logger.debug(`[ClassScheduler] handleActualItemDrop called with:`, {
            droppedItemId,
            targetRoomId,
            targetRoomName,
            targetDay,
            newStartTimeString,
            draggedItemInfo
        });

        // Check both the drag metadata and the original data for the type
        const itemType = draggedItemInfo.typeDnd || draggedItemInfo.entry?.typeDnd;

        logger.debug(`[ClassScheduler] Drop detected: ID=${droppedItemId}, Type=${itemType}, RoomId=${targetRoomId}, RoomName=${targetRoomName}, Day=${targetDay}, Time=${newStartTimeString}`);

        if (itemType === ItemTypes.COURSE) { // Legacy code path for templates (currently unused)
            logger.debug(`[ClassScheduler] Creating new instance from Course Definition: ID=${draggedItemInfo.entry?.id}`);
            const courseDef = draggedItemInfo.entry; // This is now a ScheduleEntry instance

            if (!courseDef || !courseDef.durationMinutes) { // Check durationMinutes from the ScheduleEntry template
                console.error("Cannot create instance: Course definition or its duration is invalid.", courseDef);
                handleGlobalDragEnd();
                return;
            }

            // const { durationMinutes } = parseTime(courseDef.time); // Old way, courseDef.time was duration string
            const durationMinutes = courseDef.durationMinutes; // Directly use durationMinutes
            const { startMinutes: newStartMinutes } = parseTime(newStartTimeString);

            if (durationMinutes == null || newStartMinutes == null) {
                console.error("Cannot parse time for new instance or target slot.");
                handleGlobalDragEnd();
                return;
            }

            const newEndTimeString = formatTimeFromMinutes(newStartMinutes + durationMinutes);

            // Create a template item for the modal with the drop location and duration
            // Preserve the original ID in additional props so we can remove it from unscheduled list
            const templateItem = new ScheduleEntry({
                id: `temp-add-${Date.now()}`,
                day: targetDay,
                timeStart: newStartTimeString,
                timeEnd: newEndTimeString,
                course: courseDef.course || '',
                teacher: courseDef.teacher || '',
                group: courseDef.group || '',
                groups: courseDef.groups || [],
                room: targetRoomName,
                room_id: targetRoomId,
                type: courseDef.type || 'class',
                weekSeries: courseDef.weekSeries || '1-53',
                period_id: selectedPeriodId ? Number(selectedPeriodId) : null,
                // DON'T automatically set timePeriodId - preserve original weekSeries intention
                durationMinutes: durationMinutes,
                originalUnscheduledId: courseDef.id // Store the original ID for cleanup
            });

            // Open the modal with the pre-filled template (auto-save pending changes first in draft mode)
            await openModalWithAutosave(templateItem);
            handleGlobalDragEnd();

        } else { // Item dragged from calendar OR unscheduled container (existing scheduled instance)
            logger.debug(`[ClassScheduler] Moving existing scheduled item: ID=${droppedItemId}`);

            if (draggedItemInfo.entry && isSecondaryPeriodEntry(draggedItemInfo.entry)) {
                const { startMinutes: newStartMinutes } = parseTime(newStartTimeString);
                const durationMinutes = draggedItemInfo.entry.durationMinutes;
                if (newStartMinutes == null || durationMinutes == null) {
                    console.error('[ClassScheduler] Cannot move secondary-period item: invalid time or duration', {
                        droppedItemId,
                        newStartTimeString,
                        durationMinutes
                    });
                    handleGlobalDragEnd();
                    return;
                }

                const updatedEntry = draggedItemInfo.entry.clone();
                updatedEntry.day = targetDay;
                updatedEntry.room = targetRoomName;
                updatedEntry.roomObj = targetRoom || null;
                updatedEntry.timeStart = newStartTimeString;
                updatedEntry.timeEnd = formatTimeFromMinutes(newStartMinutes + durationMinutes);

                try {
                    await persistSecondaryPeriodEntry(updatedEntry, draggedItemInfo.entry, 'move');
                } catch (error) {
                    logger.error('[ClassScheduler] Failed to move secondary-period entry:', error);
                    setError(error?.message || 'Failed to update entry from another period');
                } finally {
                    handleGlobalDragEnd();
                }
                return;
            }

            // Use the handler from the hook for moving existing items
            handleCalendarItemDrop(droppedItemId, targetRoomId, targetDay, newStartTimeString);
        }
        // handleGlobalDragEnd(); // The specific handlers (handleCalendarItemDrop or logic above) should call this.
    }, [draggedItemInfo, selectedPeriodId, selectedYearForView, handleGlobalDragEnd, handleCalendarItemDrop, setSelectedScheduleItem, setIsScheduleModalOpen, openModalWithAutosave, isSecondaryPeriodEntry, persistSecondaryPeriodEntry, setError, rooms]);

    // --- Resize Handler ---
    const handleItemResize = useCallback(async (itemId, newStartTime, newEndTime) => {
        logger.debug(`[ClassScheduler] handleItemResize called:`, {
            itemId,
            newStartTime,
            newEndTime
        });

        // Find the item to resize
        const itemToResize = scheduledInstances.find(item => item.id === itemId);
        if (!itemToResize) {
            const secondaryItemToResize = [...overlappingPeriodLiveEntries, ...extraPeriodLiveEntries].find(item => String(item.id) === String(itemId));
            if (secondaryItemToResize && isSecondaryPeriodEntry(secondaryItemToResize)) {
                const updatedItem = secondaryItemToResize.clone();
                updatedItem.timeStart = newStartTime;
                updatedItem.timeEnd = newEndTime;

                try {
                    await persistSecondaryPeriodEntry(updatedItem, secondaryItemToResize, 'resize');
                } catch (error) {
                    logger.error('[ClassScheduler] Failed to resize secondary-period entry:', error);
                    setError(error?.message || 'Failed to update entry from another period');
                }
                return;
            }

            console.error(`[ClassScheduler] Item not found for resize: ${itemId}`);
            return;
        }

        // Create updated item with new times
        const updatedItem = itemToResize.clone();
        updatedItem.timeStart = newStartTime;
        updatedItem.timeEnd = newEndTime;

        // Update the schedule instances
        const updatedInstances = scheduledInstances.map(item =>
            item.id === itemId ? updatedItem : item
        );

        console.log('*** HANDLEITEMRESIZE - UPDATING INSTANCES ***', {
            itemId,
            currentInstancesLength: scheduledInstances.length,
            updatedInstancesLength: updatedInstances.length,
            sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
        });

        // Update state immediately for responsive UI (Requirement 8.4)
        setScheduledInstances(updatedInstances);

        // Add to change tracking log (Requirement 8.3)
        addLogEntry('modify', itemToResize, updatedItem);

        // Recalculate conflicts for the modified entry and affected entries (Requirements 7.1, 7.2, 7.3)
        try {
            const entriesWithUpdatedConflicts = await conflictManager.recalculateConflictsForChangedEntries(
                [itemId],
                updatedInstances,
                teachers,
                groups,
                subjects,
                { peerEntries: buildConflictPeerEntries(updatedInstances) }
            );

            console.log('*** HANDLEITEMRESIZE - CONFLICT RECALCULATION RESULT ***', {
                entriesWithUpdatedConflictsLength: entriesWithUpdatedConflicts.length,
                sampleIds: entriesWithUpdatedConflicts.slice(0, 5).map(item => item.id)
            });

            // Update state with conflict information using functional merge to preserve ID swaps
            const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
            setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));

            logger.debug(`[ClassScheduler] Resize completed with conflict recalculation for item: ${itemId}`);
        } catch (error) {
            console.error(`[ClassScheduler] Error recalculating conflicts after resize:`, error);
            // Keep the time changes even if conflict calculation fails
        }
    }, [scheduledInstances, teachers, groups, subjects, addLogEntry, overlappingPeriodLiveEntries, extraPeriodLiveEntries, isSecondaryPeriodEntry, persistSecondaryPeriodEntry, setError, buildConflictPeerEntries]);

    // --- Modal Handlers ---
    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalOpen(false);
        setSelectedScheduleItem(null);
    }, []);

    const handleSaveEdit = useCallback(async (editedDataObj, originalItem) => {
        setIsSaveAnimationActive(true);
        try {
            // Build merged entry
            const sourceObj = originalItem && typeof originalItem.toObject === 'function' ? originalItem.toObject() : (originalItem || {});
            const mergedData = { ...(sourceObj || {}), ...(editedDataObj || {}) };
            const mergedEntry = new ScheduleEntry(mergedData);

            // Check if this is a new item (for highlighting logic)
            const isNewItem = !originalItem || String(originalItem.id).startsWith('temp-add-');

            if (!isNewItem && isSecondaryPeriodEntry(originalItem || mergedEntry)) {
                try {
                    await persistSecondaryPeriodEntry(mergedEntry, originalItem, 'modify');
                } catch (error) {
                    logger.error('[ClassScheduler] Failed to modify secondary-period entry:', error);
                    setError(error?.message || 'Failed to update entry from another period');
                }
                return;
            }

            // Compute updated items list
            const prevItems = Array.isArray(scheduledInstances) ? scheduledInstances : [];
            const exists = prevItems.some(it => String(it.id) === String(mergedEntry.id));
            const updatedItems = exists
                ? prevItems.map(it => String(it.id) === String(mergedEntry.id) ? mergedEntry : it)
                : [...prevItems, mergedEntry];

            // Update local state for responsive UI
            setScheduledInstances(updatedItems);

            // Change tracking
            if (!exists) {
                addLogEntry('create', null, mergedEntry);
            } else {
                addLogEntry('modify', originalItem, mergedEntry);
            }

            // Set highlighted item ID for scrolling and highlighting (only for new items and scheduled items)
            if (isNewItem && mergedEntry.isScheduled()) {
                setHighlightedItemIds([mergedEntry.id]);
            }

            if (currentDraftName) {
                await handleSaveChanges(updatedItems, saveChangesToAPI, () => {
                    setDraftRefreshTrigger(prev => prev + 1);
                    setIsBaselineSet(false);
                    setHasInitialConflictsCalculated(false);
                });
            }

            // Recalculate conflicts in both modes
            if (isAdmin) {
                conflictManager.recalculateConflictsForChangedEntries(
                    [mergedEntry.id],
                    updatedItems,
                    teachers,
                    groups,
                    subjects,
                    { peerEntries: buildConflictPeerEntries(updatedItems) }
                ).then(entriesWithUpdatedConflicts => {
                    const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
                    setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));
                }).catch(() => { });
            }
        } finally {
            closeScheduleModal();
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    }, [currentDraftName, scheduledInstances, setScheduledInstances, addLogEntry, handleSaveChanges, saveChangesToAPI, setDraftRefreshTrigger, setIsBaselineSet, setHasInitialConflictsCalculated, isAdmin, teachers, groups, subjects, closeScheduleModal, isSecondaryPeriodEntry, persistSecondaryPeriodEntry, setError, buildConflictPeerEntries]);

    // --- Time Period Management Handlers ---
    // Period management has been moved to Year Structure Editor
    // These handlers are no longer needed

    // Placeholder for opening item modal
    const handleOpenModal = useCallback(async (item) => {
        if (isLiveViewOnly) {
            requestForkFromLive();
            return;
        }
        logger.debug("Open modal for (ClassSchedulerPage):", item);
        await openModalWithAutosave(item);
    }, [isLiveViewOnly, openModalWithAutosave, requestForkFromLive]);

    // Duplicate an existing scheduled item (creates a new entry with a new ID)
    const handleDuplicateItem = useCallback((item) => {
        if (isLiveViewOnly) {
            requestForkFromLive();
            return;
        }
        try {
            const sourceObj = typeof item?.toObject === 'function' ? item.toObject() : item;
            const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            const newItemData = { ...sourceObj, id: newItemId };
            const newEntry = new ScheduleEntry(newItemData);

            setScheduledInstances(prevItems => {
                console.log('*** HANDLEDUPLICATEITEM - ADDING DUPLICATE ***', {
                    prevItemsLength: prevItems.length,
                    newItemId: newEntry.id,
                    originalItemId: item.id,
                    sampleIds: prevItems.slice(0, 5).map(item => item.id)
                });

                const updatedItems = [...prevItems, newEntry];

                console.log('*** HANDLEDUPLICATEITEM - ADDED DUPLICATE RESULT ***', {
                    updatedItemsLength: updatedItems.length,
                    sampleIds: updatedItems.slice(0, 5).map(item => item.id)
                });

                if (isAdmin) {
                    conflictManager.recalculateConflictsForChangedEntries(
                        [newEntry.id],
                        updatedItems,
                        teachers,
                        groups,
                        subjects,
                        { peerEntries: buildConflictPeerEntries(updatedItems) }
                    ).then(entriesWithUpdatedConflicts => {
                        console.log('*** HANDLEDUPLICATEITEM - CONFLICT RECALCULATION RESULT ***', {
                            entriesWithUpdatedConflictsLength: entriesWithUpdatedConflicts.length,
                            sampleIds: entriesWithUpdatedConflicts.slice(0, 5).map(item => item.id)
                        });
                        const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
                        setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));
                    });
                }
                return updatedItems;
            });

            addLogEntry('create', null, newEntry);
            logger.debug('Item duplicated', { from: item?.id, to: newEntry.id });
        } catch (error) {
            console.error('Failed to duplicate item:', error);
        }
    }, [isAdmin, isLiveViewOnly, requestForkFromLive, teachers, groups, subjects, addLogEntry, buildConflictPeerEntries]);

    const handleCopyToCurrentDraft = useCallback((entry) => {
        if (isLiveViewOnly) {
            requestForkFromLive();
            return;
        }
        setScheduledInstances(prevItems => [...prevItems, entry]);
        addLogEntry('create', null, entry);
    }, [addLogEntry, isLiveViewOnly, requestForkFromLive, setScheduledInstances]);

    const handleRemoveFromCurrentDraft = useCallback((entryId) => {
        setScheduledInstances((prevItems) => prevItems.filter((item) => String(item.id) !== String(entryId)));
    }, [setScheduledInstances]);

    // Core deletion logic - shared by both grid and modal deletions
    const deleteScheduleItem = useCallback(async (scheduleItem) => {
        console.log('*** DELETESCHEDULEITEM CALLED ***', {
            itemId: scheduleItem.id,
            itemCourse: scheduleItem.course,
            currentScheduledInstancesLength: scheduledInstances.length
        });

        logger.debug('Deleting schedule item:', scheduleItem.id);

        if (isSecondaryPeriodEntry(scheduleItem)) {
            if (!confirmSecondaryPeriodChange(scheduleItem, 'delete')) {
                return;
            }

            try {
                await assetManager.deleteScheduleEntry(scheduleItem.id);
                setOverlappingPeriodLiveEntries(prevEntries =>
                    prevEntries.filter(entry => String(entry.id) !== String(scheduleItem.id))
                );

                const periodName = getPeriodDisplayName(getScheduleEntryPeriodId(scheduleItem));
                setSuccess(`Deleted entry from ${periodName}`);
                setTimeout(() => setSuccess(null), 3000);
            } catch (error) {
                logger.error('[ClassScheduler] Failed to delete secondary-period entry:', error);
                setError(error?.message || 'Failed to delete entry from another period');
            }
            return;
        }

        // Remove the item from scheduledInstances
        const updatedInstances = scheduledInstances.filter(item => item.id !== scheduleItem.id);

        console.log('*** DELETESCHEDULEITEM - FILTERING INSTANCES ***', {
            prevInstancesLength: scheduledInstances.length,
            itemToDelete: scheduleItem.id,
            updatedInstancesLength: updatedInstances.length,
            sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
        });

        // Update local state for responsive UI
        setScheduledInstances(updatedInstances);

        // Track this item as locally deleted to prevent re-addition by filtering effects
        setLocallyDeletedItemIds(prev => {
            const newSet = new Set(prev);
            newSet.add(String(scheduleItem.id));
            console.log('*** DELETESCHEDULEITEM - ADDED TO LOCALLY DELETED SET ***', {
                itemId: scheduleItem.id,
                locallyDeletedCount: newSet.size
            });
            return newSet;
        });

        // Add to change log for tracking
        addLogEntry('delete', scheduleItem, null);

        if (currentDraftName) {
            await handleSaveChanges(updatedInstances, saveChangesToAPI, () => {
                setDraftRefreshTrigger(prev => prev + 1);
                setIsBaselineSet(false);
                setHasInitialConflictsCalculated(false);
            });
        }

        // Show success message
        setSuccess('Schedule item deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
    }, [addLogEntry, setSuccess, scheduledInstances, currentDraftName, handleSaveChanges, saveChangesToAPI, setDraftRefreshTrigger, setIsBaselineSet, setHasInitialConflictsCalculated, isSecondaryPeriodEntry, confirmSecondaryPeriodChange, assetManager, getPeriodDisplayName, getScheduleEntryPeriodId, setError]);

    // Delete schedule item from grid view (inline deletion)
    const handleDeleteFromGrid = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
    }, [deleteScheduleItem]);

    // Delete schedule item from modal view (closes modal after deletion)
    const handleDeleteFromModal = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
        closeScheduleModal();
    }, [deleteScheduleItem, closeScheduleModal]);

    // Delete unscheduled item from sidebar
    const handleDeleteUnscheduled = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
    }, [deleteScheduleItem]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsImporting(true);
            setImportError(null);
            setImportSuccess(null);

            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const fileExtension = file.name.split('.').pop().toLowerCase();

                    // Generate a draft name based on the file name and timestamp
                    const baseName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
                    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
                    const draftName = `Import-${baseName}-${timestamp}`;

                    let result;
                    if (fileExtension === 'csv') {
                        // Handle CSV file - import into draft
                        const csvData = e.target.result;
                        logger.debug('[Frontend] CSV Data type:', typeof csvData);
                        logger.debug('[Frontend] CSV Data length:', csvData ? csvData.length : 'N/A');
                        logger.debug('[Frontend] CSV Data sample:', csvData ? csvData.substring(0, 200) : 'N/A');
                        result = await draftManager.importIntoDraft(draftName, csvData, 'csv', selectedYearForView);
                    } else if (fileExtension === 'json') {
                        // Handle JSON file - import into draft
                        const scheduleEntries = JSON.parse(e.target.result);
                        result = await draftManager.importIntoDraft(draftName, scheduleEntries, 'json', selectedYearForView);
                    } else {
                        throw new Error('Unsupported file format. Please upload a .json or .csv file.');
                    }

                    setImportSuccess({
                        successful: result.successful_imports_count,
                        failed: result.failed_imports_count,
                        details: result.details,
                        draftName: draftName
                    });

                    // The DraftManager has switched to the new draft internally,
                    // and our listener will automatically update the component state
                    logger.debug('[ClassSchedulerPage] Import completed, DraftManager listener will handle mode switch');

                    // Show success message with draft information
                    setSuccess(`Successfully imported ${result.successful_imports_count} entries into draft "${draftName}". You are now viewing the draft.`);
                    setTimeout(() => setSuccess(null), 5000);

                } catch (error) {
                    console.error('Import failed:', error);
                    // Check if the error response contains detailed information
                    let errorMessage = 'Failed to import schedule entries';
                    if (error.response?.data?.error) {
                        const { line, reason } = error.response.data.error;
                        errorMessage = `Error on line ${line || 'unknown'}: ${reason}`;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    setImportError(errorMessage);
                } finally {
                    setIsImporting(false);
                }
            };

            reader.onerror = () => {
                setImportError('Failed to read the file');
                setIsImporting(false);
            };

            reader.readAsText(file);
        } catch (error) {
            console.error('File handling error:', error);
            setImportError(error.message || 'Failed to process the file');
            setIsImporting(false);
        }

        // Clear the file input so the same file can be uploaded again if needed
        event.target.value = '';
    };

    const handleExport = async () => {
        if (!user?.token) {
            setImportError('Authentication required for export');
            return;
        }

        setIsExporting(true);
        try {
            const filters = {
                timePeriodId: selectedPeriodId,
                // Add draft_name filter based on current mode
                draft_name: currentDraftName,
                // Add any other filters you want to include
            };

            const blob = await exportScheduleEntries(filters);

            // Additional client-side filtering to ensure no placeholder entries are exported
            const blobText = await blob.text();
            const scheduleData = JSON.parse(blobText);

            // Filter out placeholder entries
            const filteredData = scheduleData.filter(entry =>
                entry.subject_name !== '__PLACEHOLDER__' &&
                entry.teacher_name !== '__PLACEHOLDER__' &&
                entry.room_name !== '__PLACEHOLDER__'
            );

            // Log filtering results
            const placeholderCount = scheduleData.length - filteredData.length;
            if (placeholderCount > 0) {
                console.log(`[Export] Filtered out ${placeholderCount} placeholder entries from export`);
            } else {
                console.log('[Export] No placeholder entries found - export clean');
            }

            // Create a new blob with filtered data
            const filteredBlob = new Blob([JSON.stringify(filteredData, null, 2)], { type: 'application/json' });

            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(filteredBlob);
            const a = document.createElement('a');
            a.href = url;

            // Generate filename based on current mode
            const modeSuffix = `draft-${currentDraftName}`;
            a.download = `schedule-export-${modeSuffix}-${new Date().toISOString().split('T')[0]}.json`;

            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Export failed:', error);
            setImportError('Failed to export schedule: ' + error.message);
        } finally {
            setIsExporting(false);
        }
    };

    // New handler to remove a scheduled item when dropped into the unscheduled container
    const handleRemoveScheduledItem = useCallback((itemId) => {
        logger.debug(`[ClassScheduler] Removing scheduled item: ID=${itemId}`);

        const itemToRemove = scheduledInstances.find(item => String(item.id) === String(itemId));
        // Also check courseDefinitions if the item might have been a template directly dragged.
        // However, handleRemoveScheduledItem is usually called for items *from the schedule*.
        const definitionContextItem = courseDefinitions.find(def => def.id === itemId);


        if (!itemToRemove && !definitionContextItem) {
            console.error(`Cannot find item with ID: ${itemId} in scheduledInstances or courseDefinitions.`);
            handleGlobalDragEnd();
            return;
        }

        // Prefer itemToRemove if found (it was on the schedule), otherwise use definitionContextItem
        // This logic might need refinement based on where items can be dragged from to the unscheduled bin
        const sourceItem = itemToRemove || definitionContextItem;


        // Defer state updates to allow react-dnd to finish its current cycle
        setTimeout(() => {
            // Create ScheduleEntry instances for change tracking
            const beforeItem = sourceItem instanceof ScheduleEntry ? sourceItem : new ScheduleEntry(sourceItem);
            const afterItem = beforeItem.unschedule(); // Use the new unschedule method

            logger.debug(`[ClassScheduler] UNSCHEDULING DEBUG for item ${itemId}:`);
            logger.debug(`[ClassScheduler] BEFORE unschedule:`, {
                id: beforeItem.id,
                day: beforeItem.day,
                timeStart: beforeItem.timeStart,
                timeEnd: beforeItem.timeEnd,
                room: beforeItem.room,
                isScheduled: beforeItem.isScheduled()
            });
            logger.debug(`[ClassScheduler] AFTER unschedule:`, {
                id: afterItem.id,
                day: afterItem.day,
                timeStart: afterItem.timeStart,
                timeEnd: afterItem.timeEnd,
                room: afterItem.room,
                isScheduled: afterItem.isScheduled()
            });

            // Add to change log to track the unscheduling
            addLogEntry('modify', beforeItem, afterItem);

            // Update the item in scheduledInstances to be unscheduled (don't remove it completely)
            if (itemToRemove) {
                setScheduledInstances(prevInstances => {
                    console.log('*** HANDLEREMOVESCHEDULEDITEM - UNSCHEDULING ITEM ***', {
                        itemId,
                        prevInstancesLength: prevInstances.length,
                        sampleIds: prevInstances.slice(0, 5).map(item => item.id)
                    });

                    const updatedInstances = prevInstances.map(item => item.id === itemId ? afterItem : item);

                    console.log('*** HANDLEREMOVESCHEDULEDITEM - UNSCHEDULED RESULT ***', {
                        updatedInstancesLength: updatedInstances.length,
                        sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
                    });

                    return updatedInstances;
                });
                logger.debug(`[ClassScheduler] Updated item ${itemId} to unscheduled state in scheduledInstances`);
            } else {
                // If it wasn't in scheduledInstances, add the unscheduled version
                setScheduledInstances(prevInstances => {
                    console.log('*** HANDLEREMOVESCHEDULEDITEM - ADDING UNSCHEDULED ITEM ***', {
                        itemId,
                        prevInstancesLength: prevInstances.length,
                        sampleIds: prevInstances.slice(0, 5).map(item => item.id)
                    });

                    const updatedInstances = [...prevInstances, afterItem];

                    console.log('*** HANDLEREMOVESCHEDULEDITEM - ADDED UNSCHEDULED RESULT ***', {
                        updatedInstancesLength: updatedInstances.length,
                        sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
                    });

                    return updatedInstances;
                });
                logger.debug(`[ClassScheduler] Added unscheduled item ${itemId} to scheduledInstances`);
            }

            // Don't update courseDefinitions here - it represents server state and should only be updated
            // when fetching from server or after saving. Local changes only affect scheduledInstances.

            logger.debug("Item unscheduled/moved to definitions:", afterItem);
            handleGlobalDragEnd();
        }, 0);

    }, [scheduledInstances, courseDefinitions, setScheduledInstances, handleGlobalDragEnd, addLogEntry]);

    // Get available academic years from schedule data (start years of academic years)
    // availableYears provided by useClassSchedulerState

    // Set default room for room view when rooms are available (kept)

    // Memoize selected period object for room occupancy view
    const selectedPeriodForRoomView = useMemo(() => {
        if (!timePeriods.length) return null;
        if (selectedPeriodId) {
            const found = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
            if (found) return found;
        }
        return timePeriods[0];
    }, [selectedPeriodId, timePeriods]);

    // Set default teacher for teacher view when teachers are available
    useEffect(() => {
        if (teachers.length > 0 && !selectedTeacherForView) {
            setSelectedTeacherForView(teachers[0].name);
        }
    }, [teachers, selectedTeacherForView]);

    // Set default year based on available data
    useEffect(() => {
        if (availableYears.length > 0 && !availableYears.includes(selectedYearForView)) {
            // Set to the most recent year that has data
            const yearsWithData = availableYears.filter(year =>
                scheduledInstances.some(instance => {
                    if (!instance || typeof instance.resolvePeriodScope !== 'function') return false;
                    const scope = instance.resolvePeriodScope(yearStructure ? { ...yearStructure, periods: timePeriods } : { periods: timePeriods });
                    return scope?.academicYearStart === year;
                })
            );
            if (yearsWithData.length > 0) {
                setSelectedYearForView(Math.max(...yearsWithData));
            }
        }
    }, [availableYears, selectedYearForView, scheduledInstances, timePeriods, yearStructure]);

    // Scroll tracking effect for fixed day header
    useEffect(() => {
        // Only set up scroll listener after loading is complete and activeDays is available
        if (isLoading || !activeDays || activeDays.length === 0) return;

        let ticking = false;

        // Create a throttled scroll handler to improve performance
        const scrollHandler = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    if (!scrollContainerRef.current) {
                        ticking = false;
                        return;
                    }

                    const scrollTop = scrollContainerRef.current.scrollTop;
                    let foundDay = activeDays[0]; // Default to the first day

                    // Safety check for activeDays
                    if (!activeDays || activeDays.length === 0) {
                        ticking = false;
                        return;
                    }

                    // Iterate through day headers to find the one currently at the top
                    for (const day of activeDays) {
                        const headerElement = dayHeaderRefs.current[day];
                        if (headerElement) {
                            const offsetTop = headerElement.offsetTop;
                            const headerHeight = headerElement.offsetHeight;
                            const activationOffset = headerHeight * 4;

                            // Add 60px offset to make day header switch earlier
                            if (scrollTop >= offsetTop - activationOffset - 60) {
                                foundDay = day;
                            } else {
                                break;
                            }
                        }
                    }

                    // Use functional updates to avoid stale closures
                    setCurrentVisibleDay(currentDay => {
                        if (foundDay !== currentDay) {
                            setPreviousDay(currentDay);
                            return foundDay;
                        }
                        return currentDay;
                    });

                    // Header hide/show logic based on inner vertical scroll
                    try {
                        const last = lastInnerScrollTopRef.current || 0;
                        const delta = scrollTop - last;
                        const smallThreshold = 5; // ignore tiny jitters

                        if (delta > smallThreshold) {
                            // Scrolling down inside scheduler
                            if (!setupHeaderHiddenRef.current) {
                                window.scrollTo({ top: 100, behavior: 'smooth' }); // hide Setup header
                                setupHeaderHiddenRef.current = true;
                                maxDownInnerScrollTopRef.current = scrollTop;
                            } else if (scrollTop > maxDownInnerScrollTopRef.current) {
                                maxDownInnerScrollTopRef.current = scrollTop;
                            }
                        } else if (delta < -smallThreshold) {
                            // Scrolling up inside scheduler
                            if (setupHeaderHiddenRef.current) {
                                // Reveal header when inner scroll reaches the very top
                                if (scrollTop <= 1) {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    setupHeaderHiddenRef.current = false;
                                    maxDownInnerScrollTopRef.current = 0;
                                }
                            }
                        }

                        lastInnerScrollTopRef.current = scrollTop;
                    } catch (_) { /* no-op */ }

                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add listener with a delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', scrollHandler);
                scrollHandler(); // Initial call to set correct day
            } else {
                console.warn('[ClassSchedulerPage] scrollContainer ref is still null after loading completed');
            }
        }, 100); // Increased delay to ensure DOM is fully ready

        return () => {
            clearTimeout(timer);
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', scrollHandler);
            }
        };
    }, [isLoading, activeDays]);

    // Scroll to highlighted item's day and highlight it
    useEffect(() => {
        if (!highlightedItemIds?.length || !scrollContainerRef.current) return;

        const highlightedItem = highlightedItemIds
            .map(id => scheduledInstances.find(item => String(item.id) === String(id)))
            .find(item => item && item.day);
        if (!highlightedItem || !highlightedItem.day) return;

        const itemDay = highlightedItem.day;

        if (itemDay !== currentVisibleDay) {
            const dayHeader = dayHeaderRefs.current[itemDay];
            if (dayHeader && scrollContainerRef.current) {
                const headerTop = dayHeader.offsetTop;
                scrollContainerRef.current.scrollTo({
                    top: headerTop - 20,
                    behavior: 'smooth'
                });

                logger.debug(`[ClassSchedulerPage] Scrolling to day: ${itemDay} for highlighted items:`, highlightedItemIds);
            }
        }

        const timer = setTimeout(() => {
            setHighlightedItemIds([]);
        }, 2500);

        return () => clearTimeout(timer);
    }, [highlightedItemIds, scheduledInstances, currentVisibleDay]);

    // Week filtering handlers
    const handleWeekChange = (weekData) => {


        setSelectedWeek(weekData);
        logger.debug('[ClassSchedulerPage] Week selection changed:', weekData);

        if (weekData && typeof weekData.week === 'number') {
            setTimeFrameMode(TIME_FRAME_SINGLE_WEEK);
            setTimelineViewRange(createTimelineViewRange({
                startWeek: weekData.week,
                startWeekYear: weekData.year,
                endWeek: weekData.week,
                endWeekYear: weekData.year
            }));
        }
        if (weekData && selectedMonth) {
            setSelectedMonth(null);
        }

        // Clear teacher selection when week is selected (for teacher view)
        if (weekData && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        // Reset baseline when switching weeks so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    const handleNavigateToConflict = (target) => {
        const week = target?.week;
        const year = target?.year;
        if (typeof week === 'number') {
            handleWeekChange({ week, year });
        }
        const ids = Array.isArray(target?.itemIds) ? target.itemIds.filter(Boolean) : [];
        setIsConflictListVisible(false);
        setHighlightedItemIds([]);
        window.setTimeout(() => {
            setHighlightedItemIds(ids);
        }, 80);
    };

    // Month filtering handlers
    const handleMonthChange = (monthData) => {
        // Only show confirmation if there are unsaved changes AND we're actually changing to a different month
        // Don't show confirmation when clearing selections (monthData is null)


        setSelectedMonth(monthData);
        logger.debug('[ClassSchedulerPage] Month selection changed:', monthData);

        if (monthData && selectedWeek) {
            setSelectedWeek(null);
        }

        // Clear teacher selection when month is selected (for teacher view)
        if (monthData && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        // Reset baseline when switching months so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    const loadActiveDraftForPeriod = useCallback(async (period) => {
        if (!period || !draftManager) return false;

        const visibleDrafts = (drafts) => (drafts || []).filter(draft => !isHiddenFromVersions(draft));

        const findActiveDraft = (drafts) => (drafts || [])
            .find(draft => {
                const draftId = getDraftId(draft);
                const activeDraftId = getPeriodActiveDraftId(period);
                return activeDraftId != null && draftId != null && String(draftId) === String(activeDraftId);
            });

        const findDraftForPeriod = (drafts) => visibleDrafts(drafts)
            .find(draft => {
                const draftPeriodId = getDraftPeriodId(draft);
                return draftPeriodId != null && String(draftPeriodId) === String(period.id);
            });

        setIsLoading(true);
        try {
            let loadedDraftWasActive = getPeriodActiveDraftId(period) != null;
            let activeDraft = findActiveDraft(draftsForPeriodLock.length ? draftsForPeriodLock : draftManager.drafts);

            if (!activeDraft) {
                const refreshedDrafts = await draftManager.fetchDrafts();
                if (Array.isArray(refreshedDrafts)) {
                    setDraftsForPeriodLock(refreshedDrafts);
                }
                activeDraft = findActiveDraft(refreshedDrafts);

                if (!activeDraft && getPeriodActiveDraftId(period) == null) {
                    activeDraft = findDraftForPeriod(refreshedDrafts);
                }
            }

            if (!activeDraft && getPeriodActiveDraftId(period) == null) {
                const existingDrafts = draftsForPeriodLock.length ? draftsForPeriodLock : draftManager.drafts;
                const draftName = generateVersionName(period, existingDrafts);
                const displayName = buildDefaultDisplayNameForPeriod(period);
                const draftYear = getPeriodCalendarYear(period) ?? selectedYearForView;

                await draftManager.createEmptyDraft(draftName, draftYear, period.id, { display_name: displayName });
                const draftsAfterCreate = Array.isArray(draftManager.drafts) ? draftManager.drafts : await draftManager.fetchDrafts();
                setDraftsForPeriodLock(draftsAfterCreate);

                const createdDraft = draftsAfterCreate.find(draft => draft?.draft_name === draftName);
                if (!createdDraft?.draft_name) {
                    setError(`Created "${displayName}", but could not open it.`);
                    setTimeout(() => setError(null), 3000);
                    return false;
                }

                activeDraft = createdDraft;
                loadedDraftWasActive = false;
            }

            if (!activeDraft?.draft_name) {
                setError(`Could not find or create a draft for ${period.name || 'this period'}.`);
                setTimeout(() => setError(null), 3000);
                return false;
            }

            if (activeDraft.draft_name !== currentDraftName) {
                handleModeChange(false, activeDraft.draft_name);
                const loadedLabel = formatEditingDraftLabel(activeDraft);
                setSuccess(`${loadedDraftWasActive ? 'Loaded live draft' : 'Loaded draft'} "${loadedLabel}" for ${period.name || 'selected period'}.`);
                setTimeout(() => setSuccess(null), 3000);
            }

            return true;
        } catch (err) {
            setError(`Failed to load the active draft for ${period.name || 'this period'}: ${err.message}`);
            setTimeout(() => setError(null), 3000);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [assetManager, currentDraftName, draftManager, draftsForPeriodLock, handleModeChange, refreshTimePeriods, selectedYearForView, setError, setIsLoading, setSuccess]);

    const handleCurrentDraftDeleted = useCallback(async () => {
        const period = selectedPeriodId != null && selectedPeriodId !== ''
            ? timePeriods.find(p => String(p.id) === String(selectedPeriodId))
            : timePeriods[0];
        if (period) {
            await loadActiveDraftForPeriod(period);
        }
    }, [loadActiveDraftForPeriod, selectedPeriodId, timePeriods]);

    useEffect(() => {
        if (!isDraftManagerInitialized || isFetchingPeriods || !timePeriods.length) {
            return;
        }
        if (currentDraftName) {
            return;
        }

        const period = selectedPeriodId != null && selectedPeriodId !== ''
            ? timePeriods.find(p => String(p.id) === String(selectedPeriodId))
            : timePeriods[0];
        if (period) {
            loadActiveDraftForPeriod(period);
        }
    }, [
        currentDraftName,
        isDraftManagerInitialized,
        isFetchingPeriods,
        loadActiveDraftForPeriod,
        selectedPeriodId,
        timePeriods
    ]);

    const applyPeriodChange = useCallback(async (periodId, periodFromTimeline = null) => {
        const normalizedPeriodId = periodId == null || periodId === '' ? null : periodId;
        const matchingPeriod = normalizedPeriodId != null
            ? (periodFromTimeline || timePeriods.find(period => String(period.id) === String(normalizedPeriodId)))
            : null;
        const nextPeriodId = matchingPeriod?.id ?? normalizedPeriodId;

        if (isPeriodLockedToDraft && String(nextPeriodId) !== String(currentDraftPeriodId)) {
            const loadedActiveDraft = await loadActiveDraftForPeriod(matchingPeriod);
            if (!loadedActiveDraft) {
                return;
            }
        }

        setSelectedPeriodId(nextPeriodId);

        const nextYear = getPeriodCalendarYear(matchingPeriod);
        if (nextYear != null && nextYear !== selectedYearForView) {
            setSelectedYearForView(nextYear);
        }

        // A period chart click loads the whole period, so reset the timeframe bounds.
        setTimeFrameMode(TIME_FRAME_WHOLE_PERIOD);
        if (nextPeriodId && selectedMonth) {
            setSelectedMonth(null);
        }
        if (nextPeriodId && selectedWeek) {
            setSelectedWeek(null);
        }
        setTimelineViewRange(buildPeriodTimelineRange(matchingPeriod));

        if (nextPeriodId && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);
    }, [
        currentDraftPeriodId,
        isPeriodLockedToDraft,
        loadActiveDraftForPeriod,
        selectedMonth,
        selectedTeacherForView,
        selectedWeek,
        selectedYearForView,
        setHasInitialConflictsCalculated,
        setIsBaselineSet,
        setSelectedMonth,
        setSelectedPeriodId,
        setSelectedTeacherForView,
        setSelectedWeek,
        setSelectedYearForView,
        setTimelineViewRange,
        timePeriods,
        buildPeriodTimelineRange
    ]);

    const handlePeriodChange = useCallback(async (periodId, periodFromTimeline = null) => {
        const normalizedPeriodId = periodId == null || periodId === '' ? null : periodId;
        const matchingPeriod = normalizedPeriodId != null
            ? (periodFromTimeline || timePeriods.find(period => String(period.id) === String(normalizedPeriodId)))
            : null;
        const nextPeriodId = matchingPeriod?.id ?? normalizedPeriodId;
        const isActuallyChanging = nextPeriodId != null
            && String(nextPeriodId) !== String(selectedPeriodId);

        if (hasUnsavedChanges && isActuallyChanging) {
            setPendingPeriodChange({ periodId, periodFromTimeline });
            return;
        }

        await applyPeriodChange(periodId, periodFromTimeline);
    }, [applyPeriodChange, hasUnsavedChanges, selectedPeriodId, timePeriods]);

    const handleUnsavedPeriodPromptCancel = useCallback(() => {
        setPendingPeriodChange(null);
    }, []);

    const handleUnsavedPeriodPromptSave = useCallback(async () => {
        if (!pendingPeriodChange) return;

        setIsUnsavedPeriodPromptSaving(true);
        try {
            await handleSaveChanges(scheduledInstances, saveChangesToAPI);
            const { periodId, periodFromTimeline } = pendingPeriodChange;
            setPendingPeriodChange(null);
            await applyPeriodChange(periodId, periodFromTimeline);
        } catch (err) {
            setError(`Failed to save changes: ${err.message}`);
            setTimeout(() => setError(null), 4000);
        } finally {
            setIsUnsavedPeriodPromptSaving(false);
        }
    }, [
        applyPeriodChange,
        handleSaveChanges,
        pendingPeriodChange,
        saveChangesToAPI,
        scheduledInstances,
        setError
    ]);

    const handleUnsavedPeriodPromptDiscard = useCallback(async () => {
        if (!pendingPeriodChange) return;

        discardUnsavedChanges();
        const { periodId, periodFromTimeline } = pendingPeriodChange;
        setPendingPeriodChange(null);
        await applyPeriodChange(periodId, periodFromTimeline);
    }, [applyPeriodChange, discardUnsavedChanges, pendingPeriodChange]);

    const pendingPeriodChangeLabel = useMemo(() => {
        if (!pendingPeriodChange) return null;
        const { periodId, periodFromTimeline } = pendingPeriodChange;
        const normalizedPeriodId = periodId == null || periodId === '' ? null : periodId;
        const matchingPeriod = normalizedPeriodId != null
            ? (periodFromTimeline || timePeriods.find(period => String(period.id) === String(normalizedPeriodId)))
            : null;
        return matchingPeriod?.name || matchingPeriod?.label || 'the selected period';
    }, [pendingPeriodChange, timePeriods]);

    // Draft mode handlers
    // handleModeChange provided by useClassSchedulerState

    // handleDraftChange provided by useClassSchedulerState

    // Keep a stable reference for use before initialization sites
    useEffect(() => {
        saveChangesToAPIRef.current = saveChangesToAPI;
    }, [saveChangesToAPI]);

    // Clear locally deleted items when courseDefinitions changes (data refresh)
    useEffect(() => {
        if (courseDefinitions.length > 0) {
            console.log('*** CLEARING LOCALLY DELETED ITEMS ON DATA REFRESH ***', {
                courseDefinitionsLength: courseDefinitions.length,
                locallyDeletedCount: locallyDeletedItemIds.size
            });
            setLocallyDeletedItemIds(new Set());
        }
    }, [courseDefinitions.length]);

    // Seed scheduledInstances from courseDefinitions on initial load (or when empty)
    useEffect(() => {
        if (!isFetchingDefinitions && Array.isArray(courseDefinitions) && courseDefinitions.length > 0) {
            const currentLen = Array.isArray(scheduledInstancesRef.current) ? scheduledInstancesRef.current.length : 0;
            if (currentLen === 0) {
                logger.debug('[ClassSchedulerPage] Seeding scheduledInstances from courseDefinitions', {
                    seedLength: courseDefinitions.length,
                    isFetchingDefinitions,
                    courseDefinitionsIds: courseDefinitions.slice(0, 3).map(c => c.id)
                });
                setScheduledInstances(courseDefinitions);
            }
        }
    }, [isFetchingDefinitions, courseDefinitions, setScheduledInstances]);

    // Baseline is now set exclusively inside useChangeTracking when ready

    // Add handleSaveCurrent function
    const handleSaveCurrent = async () => {
        logger.debug('[ClassSchedulerPage] *** handleSaveCurrent CALLED ***', {
            currentDraftName,
        });

        if (isLiveViewOnly) {
            requestForkFromLive();
            return;
        }

        if (!currentDraftName) {
            // For now, just show an error - we'll handle "Save As" later if needed
            setError('No draft name specified');
            return;
        }

        setIsLoading(true);
        setIsSaveAnimationActive(true);
        try {
            // In draft mode, save changes directly to the draft
            logger.debug('[ClassSchedulerPage] Saving current state directly to draft:', currentDraftName);

            // scheduledInstances now contains both scheduled and unscheduled items
            const scheduledCount = scheduledInstances.filter(item => item.isScheduled()).length;
            const unscheduledCount = scheduledInstances.length - scheduledCount;

            logger.debug('[ClassSchedulerPage] Saving', scheduledInstances.length, 'entries to draft:', {
                scheduled: scheduledCount,
                unscheduled: unscheduledCount
            });

            if (scheduledInstances.length > 0) {
                // Use the proper handleSaveChanges function to ensure baseline is updated immediately
                logger.debug('[ClassSchedulerPage] *** CALLING handleSaveChanges WITH IMMEDIATE BASELINE UPDATE ***');
                await handleSaveChanges(scheduledInstances, saveChangesToAPI);
                // Non-blocking background refresh to ensure UI reflects server state without rebuilding page
                try { await refreshScheduleEntries(); } catch (_) { }
            }

            // Don't set baseline here - let the data refresh handle it properly
        } catch (err) {
            console.error('[ClassSchedulerPage] Error saving current state:', err);
            if (isDraftLockError(err)) {
                requestForkFromLive();
                return;
            }
            setError('Failed to save draft: ' + err.message);
        } finally {
            setIsLoading(false);
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    };

    const resolveDraftLiveChange = useCallback(async ({
        draft = null,
        makeLive,
        savePendingFirst
    } = {}) => {
        const draftName = draft?.draft_name ?? currentDraftName;
        if (!draftName) {
            setError('No draft selected');
            return null;
        }

        let draftId = getDraftId(draft) ?? (draftName === currentDraftName ? currentDraftMetadata?.id : null);
        let periodId = getDraftPeriodId(draft) ?? (draftName === currentDraftName ? currentDraftPeriodId : null) ?? selectedPeriodId;

        if ((makeLive && !draftId) || !periodId) {
            try {
                const refreshedDrafts = await draftManager.fetchDrafts();
                const refreshedDraft = refreshedDrafts.find((item) => item?.draft_name === draftName);
                draftId = draftId ?? getDraftId(refreshedDraft);
                periodId = periodId ?? getDraftPeriodId(refreshedDraft);
            } catch (refreshErr) {
                logger.warn('[ClassSchedulerPage] Failed to refresh drafts before updating live draft:', refreshErr);
            }
        }

        if (makeLive && !draftId) {
            logger.warn('[ClassSchedulerPage] Could not resolve draft id for live draft update', {
                draftName,
                periodId,
                draftMetadata: draft ?? currentDraftMetadata
            });
            setError(`Could not resolve draft id for "${draftName}". Try refreshing the page.`);
            return null;
        }
        if (!periodId) {
            setError(`This draft must be linked to a period before it can be ${makeLive ? 'made live' : 'stopped'}.`);
            return null;
        }

        const targetPeriod = timePeriods.find((period) => String(period.id) === String(periodId));
        const activeDraftId = targetPeriod?.active_schedule_draft_id ?? targetPeriod?.activeScheduleDraftId;
        const resolvedDraft = draft
            || (draftName === currentDraftName ? currentDraftMetadata : null)
            || { draft_name: draftName };
        const isHistoryRestore = Boolean(makeLive && isHistoryDraft(resolvedDraft));
        const displayName = formatDraftDisplayName(resolvedDraft) || draftName;
        if (makeLive && activeDraftId != null && String(activeDraftId) === String(draftId)) {
            setSuccess(isHistoryRestore
                ? `This snapshot is already live for this period.`
                : `"${displayName}" is already live for this period.`);
            setTimeout(() => setSuccess(null), 3000);
            return { alreadyApplied: true };
        }
        if (!makeLive && (activeDraftId == null || (draftId != null && String(activeDraftId) !== String(draftId)))) {
            return { alreadyApplied: true };
        }

        return {
            draftName,
            displayName,
            draftId,
            periodId,
            periodName: targetPeriod?.name || 'this period',
            makeLive,
            isHistoryRestore,
            savePendingFirst: savePendingFirst ?? draftName === currentDraftName,
            hadPreviousLive: activeDraftId != null
        };
    }, [
        currentDraftMetadata,
        currentDraftName,
        currentDraftPeriodId,
        draftManager,
        selectedPeriodId,
        setError,
        setSuccess,
        timePeriods
    ]);

    const executeDraftLiveChange = useCallback(async (resolved) => {
        if (!resolved || resolved.alreadyApplied) {
            return Boolean(resolved?.alreadyApplied);
        }

        const { draftName, displayName, draftId, periodId, periodName, makeLive, savePendingFirst, hadPreviousLive, isHistoryRestore } = resolved;

        setIsLoading(true);
        try {
            const shouldSavePending = makeLive && savePendingFirst && !isHistoryRestore;
            const hasPending = shouldSavePending && typeof hasPendingChangesByIdSets === 'function'
                ? hasPendingChangesByIdSets()
                : false;
            if (hasPending && scheduledInstances.length > 0) {
                await handleSaveChanges(scheduledInstances, saveChangesToAPI);
            }

            await assetManager.setPeriodActiveScheduleDraft(periodId, makeLive ? Number(draftId) : null);
            await refreshTimePeriods();
            let nextDraftName = draftName;
            try {
                const refreshed = await draftManager.fetchDrafts();
                if (Array.isArray(refreshed)) {
                    setDraftsForPeriodLock(refreshed);
                    const byId = refreshed.find((item) => draftId != null && String(item.id) === String(draftId));
                    if (byId?.draft_name) {
                        nextDraftName = byId.draft_name;
                    }
                }
            } catch (_) {
                // Keep the previous name if the list cannot be refreshed.
            }
            if (nextDraftName && (makeLive || nextDraftName !== currentDraftName)) {
                handleModeChange(false, nextDraftName);
            }
            const label = displayName || draftName;
            setSuccess(makeLive
                ? (isHistoryRestore
                    ? (hadPreviousLive
                        ? `Snapshot restored for ${periodName}. Previous live was moved to History.`
                        : `Snapshot restored for ${periodName}.`)
                    : (hadPreviousLive
                        ? `"${label}" is now live for ${periodName}. Previous live was moved to History.`
                        : `"${label}" is now live for ${periodName}.`))
                : `"${label}" was moved to History for ${periodName}.`);
            setTimeout(() => setSuccess(null), 4000);
            setDraftRefreshTrigger((prev) => prev + 1);
            return true;
        } catch (err) {
            setError(`Failed to ${makeLive ? 'make draft live' : 'stop draft'}: ${err.message}`);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [
        assetManager,
        handleSaveChanges,
        hasPendingChangesByIdSets,
        refreshTimePeriods,
        saveChangesToAPI,
        scheduledInstances,
        handleModeChange,
        currentDraftName,
        draftManager,
        setError,
        setIsLoading,
        setSuccess
    ]);

    const requestDraftLiveChange = useCallback(async ({
        draft = null,
        makeLive,
        savePendingFirst,
        skipConfirm = false,
        onAfterSuccess
    } = {}) => {
        const resolved = await resolveDraftLiveChange({ draft, makeLive, savePendingFirst });
        if (!resolved) {
            return false;
        }
        if (resolved.alreadyApplied) {
            return true;
        }

        if (skipConfirm) {
            const succeeded = await executeDraftLiveChange(resolved);
            if (succeeded && typeof onAfterSuccess === 'function') {
                onAfterSuccess();
            }
            return succeeded;
        }

        setPendingDraftLiveChange({
            ...resolved,
            onAfterSuccess
        });
        return 'pending';
    }, [executeDraftLiveChange, resolveDraftLiveChange]);

    const handleDraftLiveConfirmCancel = useCallback(() => {
        if (isDraftLiveConfirming) return;
        setPendingDraftLiveChange(null);
    }, [isDraftLiveConfirming]);

    const handleDraftLiveConfirm = useCallback(async () => {
        if (!pendingDraftLiveChange || isDraftLiveConfirming) return;

        setIsDraftLiveConfirming(true);
        try {
            const { onAfterSuccess, ...resolved } = pendingDraftLiveChange;
            const succeeded = await executeDraftLiveChange(resolved);
            if (succeeded) {
                setPendingDraftLiveChange(null);
                if (typeof onAfterSuccess === 'function') {
                    onAfterSuccess();
                }
            }
        } finally {
            setIsDraftLiveConfirming(false);
        }
    }, [executeDraftLiveChange, isDraftLiveConfirming, pendingDraftLiveChange]);

    const handleDraftLiveOpenAsVersion = useCallback(async () => {
        if (!pendingDraftLiveChange?.draftName || !draftManager || isDraftLiveConfirming) return;

        const sourceName = pendingDraftLiveChange.draftName;
        const period = timePeriods.find((item) => String(item.id) === String(pendingDraftLiveChange.periodId)) || null;
        const newName = generateVersionName(period, draftsForPeriodLock);

        setIsDraftLiveConfirming(true);
        setIsLoading(true);
        try {
            const result = await draftManager.duplicateDraft(sourceName, newName);
            const refreshed = await draftManager.fetchDrafts();
            if (Array.isArray(refreshed)) {
                setDraftsForPeriodLock(refreshed);
            }
            const created = (Array.isArray(refreshed) ? refreshed : []).find((item) => item?.draft_name === newName)
                || {
                    draft_name: newName,
                    display_name: result?.display_name || pendingDraftLiveChange.displayName,
                    version_number: result?.version_number
                };
            handleModeChange(false, newName);
            setPendingDraftLiveChange(null);
            setSuccess(`Opened as ${formatEditingDraftLabel(created)}. Live is unchanged until you make this version live.`);
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setError(`Failed to open as a new version: ${err.message}`);
        } finally {
            setIsDraftLiveConfirming(false);
            setIsLoading(false);
        }
    }, [
        draftManager,
        draftsForPeriodLock,
        handleModeChange,
        isDraftLiveConfirming,
        pendingDraftLiveChange,
        setError,
        setIsLoading,
        setSuccess,
        timePeriods
    ]);

    const handleStopDraftLive = async () => {
        if (!isCurrentDraftActiveForPeriod) {
            return;
        }
        await requestDraftLiveChange({ makeLive: false });
    };

    const handleMakeDraftLive = async () => {
        await requestDraftLiveChange({ makeLive: true, savePendingFirst: true });
    };

    // Add handlePublishDraft function with scope + strategy support
    const handlePublishDraft = async (publishConfig = {}) => {
        if (!currentDraftName) {
            setError('No draft selected to publish');
            return;
        }

        const normalizedPublishConfig = publishConfig && (
            publishConfig.weekRange !== undefined
            || publishConfig.publishMode !== undefined
            || publishConfig.reviewDecisions !== undefined
        )
            ? publishConfig
            : { weekRange: publishConfig };
        const weekRange = normalizedPublishConfig.weekRange || (
            normalizedPublishConfig.startWeek !== undefined && normalizedPublishConfig.endWeek !== undefined
                ? { startWeek: normalizedPublishConfig.startWeek, endWeek: normalizedPublishConfig.endWeek }
                : null
        );
        const publishMode = normalizedPublishConfig.publishMode || 'replace';
        const reviewDecisions = normalizedPublishConfig.reviewDecisions || {};

        setIsLoading(true);
        setIsSaveAnimationActive(true);
        try {
            // Step 1: Save the current draft first
            logger.debug('[ClassSchedulerPage] Saving draft before publishing:', currentDraftName);

            const scheduledCount = scheduledInstances.filter(item => item.isScheduled()).length;
            const unscheduledCount = scheduledInstances.length - scheduledCount;

            logger.debug('[ClassSchedulerPage] Saving', scheduledInstances.length, 'entries to draft before publishing:', {
                scheduled: scheduledCount,
                unscheduled: unscheduledCount,
                weekRange,
                publishMode,
                reviewDecisionsCount: Object.keys(reviewDecisions).length
            });

            if (scheduledInstances.length > 0) {
                await handleSaveChanges(scheduledInstances, saveChangesToAPI);
            }

            logger.debug('[ClassSchedulerPage] Publishing draft to live:', {
                draftName: currentDraftName,
                weekRange,
                publishMode,
                reviewDecisionsCount: Object.keys(reviewDecisions).length
            });
            await assetManager.loadDraft(currentDraftName, selectedYearForView, {
                weekRange,
                publishMode,
                reviewDecisions
            });

            // Step 3: Stay in draft mode (don't switch to live mode)
            const rangeMessage = weekRange
                ? ` (weeks ${weekRange.startWeek}-${weekRange.endWeek})`
                : '';
            const strategyMessage = publishMode === 'additive' ? 'additively ' : '';

            setSuccess(`Draft published ${strategyMessage}successfully${rangeMessage}!`);
            setTimeout(() => setSuccess(null), 5000);

            // Trigger data refresh to ensure UI reflects server state
            setDraftRefreshTrigger(prev => prev + 1);
        } catch (err) {
            setError('Failed to publish draft: ' + err.message);
            throw err;
        } finally {
            setIsLoading(false);
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    };

    // Display loading or error state for initial fetch
    if (!isDraftManagerInitialized || (!hasLoadedDefinitionsOnce && isFetchingDefinitions) || isFetchingPeriods || isFetchingYearStructure || isLoadingAssets) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p className="text-gray-500 text-lg">
                    Loading {
                        !isDraftManagerInitialized ? 'application state' :
                            isLoadingAssets ? 'teachers, groups, subjects, and rooms' :
                                isFetchingPeriods ? 'periods' :
                                    isFetchingYearStructure ? 'year structure' :
                                        'schedule data'
                    }...
                </p>
            </div>
        );
    }

    if (fetchDefinitionsError || fetchPeriodsError || assetsError) {
        // Special handling for missing academic year structure or periods
        if (fetchPeriodsError?.includes('No academic years found') || fetchPeriodsError?.includes('No periods found')) {
            return (
                <div className="flex flex-col items-center justify-center h-screen p-4 space-y-4">
                    <div className="text-center">
                        <p className="text-lg text-gray-700 mb-2">
                            {fetchPeriodsError?.includes('No academic years found')
                                ? "You first need to setup your year structure before you can schedule your classes"
                                : "You need to setup your academic periods before you can schedule your classes"
                            }
                        </p>
                        <Button
                            onClick={() => window.location.href = '/setup/year-structure'}
                            variant="primary"
                            className="px-6 py-2"
                        >
                            Setup Year Structure
                        </Button>
                    </div>
                </div>
            );
        }

        // Default error handling for other errors
        return (
            <div className="flex flex-col items-center justify-center h-screen p-4">
                <p className="text-red-500 text-lg mb-4">
                    Error: {fetchDefinitionsError || fetchPeriodsError || assetsError}
                </p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Try Reloading
                </Button>
            </div>
        );
    }

    // toggleDebugDrawer provided by useClassSchedulerState

    return (
        <>
            <div className="flex flex-col h-screen bg-gray-100">
                {/* Fixed Header Section */}
                <ClassSchedulerHeader
                    rooms={rooms}
                    currentDraftName={currentDraftName}
                    currentDraftLabel={formatEditingDraftLabel(currentDraftMetadata) || currentDraftName}
                    isAdmin={isAdmin}
                    selectedYearForView={selectedYearForView}
                    isImporting={isImporting}
                    isExporting={isExporting}
                    importError={importError}
                    importSuccess={importSuccess}
                    isLoading={isLoading}
                    error={error}
                    success={success}
                    setError={setError}
                    setSuccess={setSuccess}
                    setIsLoading={setIsLoading}
                    draftManager={draftManager}
                    onToggleDebug={toggleDebugDrawer}
                    onModeChange={handleModeChange}
                    onDraftChange={handleDraftChange}
                    onSaveCurrent={handleSaveCurrent}
                    onPublishDraft={handlePublishDraft}
                    onFileUpload={handleFileUpload}
                    onExport={handleExport}
                    currentView={currentView}
                    setCurrentView={setCurrentView}
                    timePeriods={timePeriods}
                    selectedPeriodId={selectedPeriodId}
                    onPeriodChange={handlePeriodChange}
                    textFilterQuery={textFilterQuery}
                    setTextFilterQuery={setTextFilterQuery}
                    allRoomsForGrid={filteredRoomsForGrid}
                    selectedRoomForView={selectedRoomForView}
                    setSelectedRoomForView={setSelectedRoomForView}
                    roomVisibilityFilter={roomVisibilityFilter}
                    setRoomVisibilityFilter={setRoomVisibilityFilter}
                    teachers={teachers}
                    selectedTeacherForView={selectedTeacherForView}
                    setSelectedTeacherForView={setSelectedTeacherForView}
                    entryTypeFilters={entryTypeFilters}
                    setEntryTypeFilters={setEntryTypeFilters}
                />

                {currentView !== 'drafts' && (
                    <PeriodTimelineChart
                        timePeriods={timePeriods}
                        yearStructure={yearStructure}
                        selectedPeriodId={selectedPeriodId}
                        selectedMonth={selectedMonth}
                        selectedWeek={selectedWeek}
                        onPeriodChange={handlePeriodChange}
                        onMonthChange={handleMonthChange}
                        onWeekChange={handleWeekChange}
                        currentView={currentView}
                        periodSelectionDisabled={isPeriodLockedToDraft}
                        periodSelectionDisabledReason={periodSelectionDisabledReason}
                        visiblePeriodIds={visiblePeriodIds}
                        onPeriodVisibilityToggle={handlePeriodVisibilityToggle}
                        timelineViewRange={timelineViewRange}
                        onTimelineRangeManualChange={handleTimelineRangeManualChange}
                        timeFrameRange={timeFrameMode}
                        onTimeFrameRangeChange={handleTimeFrameRangeChange}
                        setError={setError}
                        setSuccess={setSuccess}
                        refreshTimePeriods={refreshTimePeriods}
                    />
                )}

                {/* Scrollable Content Section */}
                <div className="flex flex-grow min-h-0">
                    {currentView === 'calendar' && rooms.length > 0 && !showCopyView && (
                        <DraftPeriodSidebar
                            currentDraftName={currentDraftName}
                            draftManager={draftManager}
                            onModeChange={handleModeChange}
                            onDraftChange={handleDraftChange}
                            onToggleDraftLive={(draft, isActive, options = {}) => requestDraftLiveChange({
                                draft,
                                makeLive: !isActive,
                                ...options
                            })}
                            onCurrentDraftDeleted={handleCurrentDraftDeleted}
                            refreshTimePeriods={refreshTimePeriods}
                            refreshScheduleEntries={refreshScheduleEntries}
                            timePeriods={timePeriods}
                            selectedPeriodId={selectedPeriodId}
                            selectedYearForView={selectedYearForView}
                            setError={setError}
                            setSuccess={setSuccess}
                            setIsLoading={setIsLoading}
                        />
                    )}

                    <div ref={scrollContainerRef} className="flex-grow min-w-0 py-4 overflow-y-auto">
                        {(hasLoadedDefinitionsOnce || !isFetchingDefinitions) && !isFetchingPeriods && !isFetchingYearStructure && !isLoadingAssets ? (
                            rooms.length === 0 ? (
                                // Show message when no rooms are available
                                <div className="flex flex-col items-center justify-center h-full p-4 space-y-4">
                                    <div className="text-center">
                                        <p className="text-lg text-gray-700 mb-2">
                                            You need to setup your rooms before you can schedule your classes
                                        </p>
                                        <Button
                                            onClick={() => window.location.href = '/setup/rooms'}
                                            variant="primary"
                                            className="px-6 py-2"
                                        >
                                            Setup Rooms
                                        </Button>
                                    </div>
                                </div>
                            ) : currentView === 'calendar' ? (
                                showCopyView ? (
                                    <CopyDraftView
                                        assetManager={assetManager}
                                        drafts={allVisibleDrafts}
                                        timePeriods={timePeriods}
                                        viewPeriodId={selectedPeriodId ?? currentDraftPeriodId}
                                        currentDraftName={currentDraftName}
                                        activeDays={activeDays}
                                        allRoomsForGrid={filteredRoomsForGrid}
                                        user={user}
                                        teachers={teachers}
                                        groups={groups}
                                        subjects={subjects}
                                        selectedWeek={selectedWeek}
                                        selectedYearForView={selectedYearForView}
                                        getViewFilteredEntries={getViewFilteredEntries}
                                        onCopyToCurrentDraft={handleCopyToCurrentDraft}
                                        onRemoveFromCurrentDraft={handleRemoveFromCurrentDraft}
                                        onError={setError}
                                        onSuccess={setSuccess}
                                    />
                                ) : (
                                    /* Calendar View - Scrollable */
                                    <SharedScrollProvider>
                                        <div className="space-y-6">
                                            {activeDays.map(day => {
                                                const itemsForDay = entryTypeFilteredScheduledInstances.filter(item => item.day === day && item.isScheduled());
                                                return (
                                                    <div key={`${day}-${selectedPeriodId || 'whole-year'}`}>
                                                        <DayTableSimple
                                                            key={`${day}-${selectedPeriodId || 'whole-year'}`}
                                                            day={day}
                                                            rooms={filteredRoomsForGrid}
                                                            items={itemsForDay}
                                                            dayScheduleEntry={entryTypeFilteredScheduledInstances}
                                                            user={user}
                                                            onOpenModal={handleOpenModal}
                                                            onItemDrop={handleActualItemDrop}
                                                            onItemResize={handleItemResize}
                                                            draggedItemInfo={draggedItemInfo}
                                                            onGlobalDragStart={handleGlobalDragStart}
                                                            onGlobalDragEnd={handleGlobalDragEnd}
                                                            availabilityOverlayData={availabilityOverlayData}
                                                            KEY_SEPARATOR={KEY_SEPARATOR}
                                                            teachers={teachers}
                                                            groups={groups}
                                                            subjects={subjects}
                                                            currentWeekNumber={singleWeekView?.week}
                                                            currentYear={singleWeekView?.year || selectedYearForView}
                                                            dragToCreateData={dragToCreateData}
                                                            onDragToCreateStart={handleDragToCreateStart}
                                                            onDragToCreateMove={handleDragToCreateMove}
                                                            onDragToCreateEnd={handleDragToCreateEnd}
                                                            onDragToCreateCancel={handleDragToCreateCancel}
                                                            onDuplicateItem={handleDuplicateItem}
                                                            onDelete={handleDeleteFromGrid}
                                                            dayHeaderRef={element => dayHeaderRefs.current[day] = element}
                                                            showMondayHeader={day === 'monday'}
                                                            isSaveAnimationActive={isSaveAnimationActive}
                                                            highlightedItemIds={highlightedItemIds}
                                                            onNavigateToConflict={handleNavigateToConflict}
                                                            showOverlappingPeriodEntries={showOverlappingPeriodEntries}
                                                            selectedPeriodId={selectedPeriodId ?? currentDraftPeriodId}
                                                            timePeriods={timePeriods}
                                                            extraVisiblePeriodId={extraVisiblePeriodId}
                                                            readOnly={isLiveViewOnly}
                                                            onReadOnlyInteract={requestForkFromLive}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SharedScrollProvider>
                                )
                            ) : currentView === 'room' ? (
                                /* Rooms Occupancy View */
                                <RoomsOccupancyView
                                    rooms={rooms}
                                    scheduleEntries={entryTypeFilteredScheduledInstances.filter(item => item.isScheduled())}
                                    selectedPeriod={selectedPeriodForRoomView}
                                    activeDays={activeDays}
                                    onTimeSlotClick={(entry) => handleOpenModal(entry)}
                                />
                            ) : currentView === 'drafts' ? (
                                <DraftManagerView
                                    currentDraftName={currentDraftName}
                                    assetManager={assetManager}
                                    draftManager={draftManager}
                                    onModeChange={handleModeChange}
                                    onDraftChange={handleDraftChange}
                                    onCurrentDraftDeleted={handleCurrentDraftDeleted}
                                    onOpenDraft={() => setCurrentView('calendar')}
                                    refreshTimePeriods={refreshTimePeriods}
                                    timePeriods={timePeriods}
                                    setError={setError}
                                    setSuccess={setSuccess}
                                    setIsLoading={setIsLoading}
                                />
                            ) : (
                                /* Teacher View */
                                <TeacherView
                                    teacher={selectedTeacherForView}
                                    scheduledInstances={entryTypeFilteredScheduledInstances.filter(item => item.isScheduled())}
                                    startWeek={36}
                                    year={selectedYearForView}
                                    user={user}
                                    rooms={rooms}
                                    onOpenModal={handleOpenModal}
                                    timelineViewRange={timelineViewRange}
                                />
                            )
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-500">Loading schedule data...</p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar - Fixed width, scrollable if needed */}
                    {currentView === 'calendar' && rooms.length > 0 && !showCopyView && (
                        <div className={`flex-none transition-all duration-300 ease-in-out ${isUnscheduledDrawerOpen || isEventDrawerOpen || isMeetingDrawerOpen ? 'w-80' : 'w-12'} py-4 px-2 relative`}>
                            {/* Toggle Buttons */}
                            <SidebarDrawerToggle
                                side="right"
                                isUnscheduledDrawerOpen={isUnscheduledDrawerOpen}
                                setIsUnscheduledDrawerOpen={setIsUnscheduledDrawerOpen}
                                isEventDrawerOpen={isEventDrawerOpen}
                                setIsEventDrawerOpen={setIsEventDrawerOpen}
                                isMeetingDrawerOpen={isMeetingDrawerOpen}
                                setIsMeetingDrawerOpen={setIsMeetingDrawerOpen}
                            />

                            {/* Unscheduled Drawer Content */}
                            <div className={`absolute top-4 left-2 right-[53px] bottom-0 transition-all duration-300 ease-in-out ${isUnscheduledDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}>
                                <UnscheduledEntryContainer
                                    unscheduledCourses={unscheduledCourses}
                                    onDragStartInternal={handleGlobalDragStart}
                                    onRemoveScheduledItem={handleRemoveScheduledItem}
                                    onDeleteUnscheduled={handleDeleteUnscheduled}
                                    draggedItemInfo={draggedItemInfo}
                                />
                            </div>

                            {/* Event Drawer Content */}
                            <div className={`absolute top-4 left-2 right-[53px] bottom-0 transition-all duration-300 ease-in-out ${isEventDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}>
                                <EventContainer
                                    baseScheduleItems={scheduledInstances}
                                    currentDate={currentDate}
                                    isAdmin={isAdmin}
                                    isPublicView={false}
                                    onOpenScheduleModal={handleOpenModal}
                                    groups={groups}
                                    subjects={subjects}
                                    teachers={teachers}
                                    rooms={rooms}
                                    activeDays={activeDays}
                                    panelTitle="Events"
                                    showTitle={true}
                                />
                            </div>

                            {/* Meeting Drawer Content */}
                            <div className={`absolute top-4 left-2 right-[53px] bottom-0 transition-all duration-300 ease-in-out ${isMeetingDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}>
                                <EventContainer
                                    baseScheduleItems={scheduledInstances}
                                    currentDate={currentDate}
                                    isAdmin={isAdmin}
                                    isPublicView={false}
                                    onOpenScheduleModal={handleOpenModal}
                                    groups={groups}
                                    subjects={subjects}
                                    teachers={teachers}
                                    rooms={rooms}
                                    activeDays={activeDays}
                                    entryType="meeting"
                                    panelTitle="Meetings"
                                    emptyMessage="No upcoming meetings."
                                    addButtonLabel="Add New Meeting"
                                    documentationId="meetings"
                                    showTitle={true}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* EditScheduleItemModal */}
                <EditScheduleItemModal
                    isOpen={isScheduleModalOpen}
                    onClose={closeScheduleModal}
                    selectedScheduleItem={selectedScheduleItem}
                    onSaveEdit={handleSaveEdit}
                    onSaveEditImmediate={handleSaveEdit}
                    onDelete={handleDeleteFromModal}
                    groups={groups}
                    subjects={subjects}
                    teachers={teachers}
                    rooms={rooms}
                    // Academic-year start year (used for exception IDs & week->year mapping)
                    currentYear={selectedYearForView}
                    currentDate={currentDate}
                    isAdmin={isAdmin}
                    baseScheduleItems={scheduledInstances}
                    onAddTeacher={handleAddTeacher}
                    onAddSubject={handleAddSubject}
                    onAddGroup={handleAddGroup}
                    currentTimeRangeContext={editModalTimeRangeContext}
                    draftMode={!!currentDraftName}
                    draftMetadata={currentDraftMetadata}
                    drafts={draftsForPeriodLock}
                    onNavigateToConflict={handleNavigateToConflict}
                />

                {/* ConflictListModal */}
                <ConflictListModal
                    isOpen={isConflictListVisible}
                    onClose={toggleConflictList}
                    conflicts={currentConflicts}
                    currentWeekNumber={singleWeekView?.week}
                    currentYear={singleWeekView?.year || selectedYearForView}
                    onNavigateToConflict={handleNavigateToConflict}
                />

                {/* Week Change Confirmation Modal - Not shown in ClassSchedulerPage due to autoApplyToAllWeeks: true */}

                <UnsavedChangesConfirmModal
                    isOpen={Boolean(pendingPeriodChange)}
                    onClose={handleUnsavedPeriodPromptCancel}
                    onSave={handleUnsavedPeriodPromptSave}
                    onDiscard={handleUnsavedPeriodPromptDiscard}
                    isSaving={isUnsavedPeriodPromptSaving}
                    message={pendingPeriodChangeLabel
                        ? `You have unsaved changes. Switch to ${pendingPeriodChangeLabel}?`
                        : 'You have unsaved changes. What would you like to do before switching period?'}
                />

                <DraftLiveConfirmModal
                    isOpen={Boolean(pendingDraftLiveChange)}
                    onClose={handleDraftLiveConfirmCancel}
                    onConfirm={handleDraftLiveConfirm}
                    onOpenAsVersion={pendingDraftLiveChange?.isHistoryRestore ? handleDraftLiveOpenAsVersion : undefined}
                    isConfirming={isDraftLiveConfirming}
                    makeLive={pendingDraftLiveChange?.makeLive ?? true}
                    restoreMode={Boolean(pendingDraftLiveChange?.isHistoryRestore)}
                    draftName={pendingDraftLiveChange?.draftName}
                    displayName={pendingDraftLiveChange?.displayName}
                    periodName={pendingDraftLiveChange?.periodName}
                    periodId={pendingDraftLiveChange?.periodId}
                    assetManager={assetManager}
                    currentDraftName={currentDraftName}
                    scheduledInstances={scheduledInstances}
                    timePeriods={timePeriods}
                    yearStructure={yearStructure}
                    timeFrameSelection={timeFrameSelection}
                    selectedWeek={selectedWeek}
                    selectedYearForView={selectedYearForView}
                    activeDays={activeDays}
                    rooms={rooms}
                    user={user}
                    teachers={teachers}
                    groups={groups}
                    subjects={subjects}
                />

                <ForkLiveDraftModal
                    isOpen={isForkLivePromptOpen}
                    onClose={() => {
                        if (!isForkingLiveDraft) setIsForkLivePromptOpen(false);
                    }}
                    onConfirm={handleForkFromLive}
                    isConfirming={isForkingLiveDraft}
                    draftName={formatDraftDisplayName(currentDraftMetadata) || currentDraftName}
                    isLive={isCurrentDraftActiveForPeriod}
                />

                {/* Floating Action Buttons */}
                {currentView !== 'drafts' && (
                    <FloatingActionButtons
                        showOverlappingPeriods={hasMultipleOverlappingPeriods && !showCopyView}
                        showOverlappingPeriodEntries={showOverlappingPeriodEntries}
                        onShowOverlappingPeriodEntriesChange={setShowOverlappingPeriodEntries}
                        overlappingPeriodCount={overlappingPeriodIds.length}
                        showCopyViewButton={canOpenCopyView}
                        showCopyView={showCopyView}
                        onShowCopyViewChange={setShowCopyView}
                        showSaveDraft={!!currentDraftName && !isLiveViewOnly}
                        showMakeLive={!!currentDraftName}
                        isDraftActiveForPeriod={isCurrentDraftActiveForPeriod}
                        onMakeLive={handleMakeDraftLive}
                        onStopLive={handleStopDraftLive}
                        showPublishDraft={false}
                        showShareSchedule={!isLiveViewOnly}
                        showEditNewVersion={isLiveViewOnly}
                        onEditNewVersion={requestForkFromLive}
                        showConflictIndicator={isAdmin}
                        isLoading={isLoading}
                        currentDraftName={currentDraftName}
                        currentDraftId={currentDraftMetadata?.id ?? null}
                        currentDraftPeriodId={currentDraftPeriodId}
                        currentConflicts={currentConflicts}
                        scheduledInstances={scheduledInstances}
                        serverBaseline={serverBaseline}
                        // New: ID-set change counts for save enablement
                        changeIdCounts={typeof getChangeIdCounts === 'function' ? getChangeIdCounts() : { created: 0, modified: 0, deleted: 0 }}
                        hasPendingChangesByIdSets={typeof hasPendingChangesByIdSets === 'function' ? hasPendingChangesByIdSets() : false}
                        // Undo/Redo functionality
                        canUndo={!isLiveViewOnly && canUndo()}
                        canRedo={!isLiveViewOnly && canRedo()}
                        onUndo={handleUndo}
                        onRedo={handleRedo}
                        onSaveCurrent={handleSaveCurrent}
                        onPublishDraft={handlePublishDraft}
                        onToggleConflictList={() => setIsConflictListVisible(prev => !prev)}
                        // Data for PublishDraftModal / ShareScheduleModal
                        timePeriods={timePeriods}
                        selectedYearForView={selectedYearForView}
                        yearStructure={yearStructure}
                        shareWeekRange={shareWeekRange}
                        institutionUrlEndpoint={assetManager?.getInstitutionUrlEndpoint && assetManager.getInstitutionUrlEndpoint()}
                    />
                )}

                {/* Debug Drawer */}
                <DebugDrawer
                    isOpen={isDebugDrawerOpen}
                    onClose={() => setIsDebugDrawerOpen(false)}
                    courseDefinitions={courseDefinitions}
                    scheduledInstances={scheduledInstances}
                    selectedPeriodId={selectedPeriodId}
                    timePeriods={timePeriods}
                    selectedYearForView={selectedYearForView}
                />

                {/* Success/Error Messages */}
                {error && (
                    <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
                        <span className="block sm:inline">{error}</span>
                        <button
                            className="float-right ml-4 text-red-500 hover:text-red-700"
                            onClick={() => setError(null)}
                        >
                            ×
                        </button>
                    </div>
                )}

                {(success || importSuccess) && (
                    <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 max-w-md">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {success && (
                                    <span className="block sm:inline">
                                        {typeof success === 'string' ? success : success.message}
                                    </span>
                                )}
                                {importSuccess && (
                                    <div>
                                        Import completed: {importSuccess.successful} successful, {importSuccess.failed} failed
                                        {importSuccess.draftName && (
                                            <div className="text-xs mt-1 text-blue-600">
                                                Created draft: "{importSuccess.draftName}"
                                            </div>
                                        )}
                                        {importSuccess.details && (
                                            <div className="text-xs mt-1">
                                                {/* Show first few failures if any */}
                                                {importSuccess.details.failures && importSuccess.details.failures.length > 0 && (
                                                    <div className="text-red-600">
                                                        Failures: {importSuccess.details.failures.slice(0, 2).map((failure, idx) => (
                                                            <div key={idx}>• {failure.reason}</div>
                                                        ))}
                                                        {importSuccess.details.failures.length > 2 &&
                                                            <div>... and {importSuccess.details.failures.length - 2} more failures</div>
                                                        }
                                                    </div>
                                                )}
                                                {/* Show some success details */}
                                                {importSuccess.details.successes && importSuccess.details.successes.length > 0 && (
                                                    <div className="text-green-600">
                                                        Successfully imported {importSuccess.details.successes.length} entries
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {typeof success === 'object' && success.action && (
                                    <div className="mt-2">
                                        <button
                                            className="text-green-600 hover:text-green-800 underline font-medium text-sm"
                                            onClick={success.action.onClick}
                                        >
                                            {success.action.label}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                className="ml-4 text-green-500 hover:text-green-700 flex-shrink-0"
                                onClick={() => {
                                    setSuccess(null);
                                    setImportSuccess(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default ClassSchedulerPage;

